import gsap from "gsap";
import { characters, getCharacter, getMovie, defaultCharacterId } from "./data/characters.js";
import {
  createViewer,
  createRenderLoop,
  prefetchModel,
  releaseModel,
  setMeshMode,
} from "./three/viewer.js";
import { gateOn, modelSettled, afterEntrance, onPageIn } from "./loading.js";

// the Google Fonts link is preloaded (not render-blocking) in the HTML head --
// promote it to an active stylesheet once the module script runs
const fontsLink = document.getElementById("fontsStylesheet");
if (fontsLink) fontsLink.rel = "stylesheet";

const renderLoop = createRenderLoop();
const app = document.getElementById("app");

// dev-only: lets a test pause and scrub gsap's global timeline, so the flight
// can be inspected at exact progress values
if (import.meta.env.DEV) window.__gsap = gsap;

// Longest the off-screen models will wait on the visible ones before being
// fetched anyway. Only reached if a visible model never resolves at all --
// otherwise the prefetch starts the moment the last visible card has drawn.
const PREFETCH_FALLBACK_MS = 8000;

const CARD_DISTANCE_SCALE = 2.05;
const MASCOT_DISTANCE_SCALE = 1.02;
const FADE_SETTLE_TIMEOUT = 320;

// How the row arrives. The easing mirrors CARD_EXIT_EASE
// so leaving and arriving belong to the same room. Longer and decelerating
// than the exit: going gets out of the way, arriving has to land.
const CARD_ENTER_SECONDS = 0.42;
// The exit's back.in(2.2), turned around. Not its number, though: back's
// overshoot is a SHARE of the distance travelled -- 15.4% at 2.2 -- and the
// exit only drops a card 84px, so its wind-up is about 13px. The same 2.2 over
// this move's ~324px would throw each card 50px past its slot and haul it
// back, which is a different gesture entirely. 1.1 overshoots by 4.5%, so the
// card sails the same ~13px past and settles: the exit's character, at the
// exit's actual size on screen.
const CARD_ENTER_EASE = "back.out(1.1)";
// Kept off the spring, exactly as the exit keeps its own fade off back.in and
// for the same reason: an overshooting curve on opacity runs past fully opaque
// and has to come back down, which on the way IN means a card that arrives,
// flickers and re-settles. Shorter than the travel too, so a card is solid
// while it is still moving rather than a ghost sliding into place.
const CARD_ENTER_FADE = 0.7;
const CARD_ENTER_FADE_EASE = "power2.out";
// Each card starts this much after the one to its left, so the row is dealt out
// rather than arriving as a slab. Twice the exit's 40ms ripple: that one
// spreads a row that is already in place, while this one has to read as four
// separate arrivals across a full card's travel.
const CARD_ENTER_STAGGER = 0.08;
// How far to the right each card starts, as a share of its own width, so it
// stays proportional at the widths where these cards are much smaller.
const CARD_ENTER_REACH = 0.9;
const CARD_ENTER_SCALE = 0.94;

const state = {
  view: "grid", // "grid" | "detail"
  detailId: defaultCharacterId,
  viewers: [], // every live viewer for the currently mounted view
};

function gradientCss(character) {
  const [a, b, c] = character.gradient;
  return `linear-gradient(135deg, ${a} 0%, ${b} 60%, ${c} 100%)`;
}

// The panel's colour, and the tint its clip stills are graded toward. Three
// unrelated trailer frames side by side brought three unrelated palettes with
// them and fought both each other and the panel; toned to one colour they read
// as a row of three rather than three separate pictures. The middle gradient
// stop is the panel's dominant tone -- the ends are its highlight and shadow.
function paintPanel(panel, character) {
  panel.style.background = gradientCss(character);
  panel.style.setProperty("--clip-tint", character.gradient[1]);
  panel.style.setProperty("--panel-ratio", character.panelRatio ?? 1.06);
}

function clearViewers() {
  for (const viewer of state.viewers) renderLoop.remove(viewer);
  state.viewers = [];
}

// The two framings a character is seen in. Single source of truth: the flight
// interpolates between exactly the values the two static views are built with,
// so the motion cannot drift from where it starts and lands.
function cardFraming(character) {
  return {
    distanceScale: CARD_DISTANCE_SCALE * (character.zoomAdjust ?? 1),
    verticalAnchor: character.verticalAnchor ?? 0,
  };
}

function detailFraming(character) {
  return {
    distanceScale: MASCOT_DISTANCE_SCALE * (character.detailZoom ?? 1),
    verticalAnchor: character.detailAnchor ?? 0,
  };
}

const lerp = (a, b, t) => a + (b - a) * t;

// Whether this device can afford to hold characters it is not showing. A phone
// cannot: holding all six kills the tab on iOS. Width is the signal because it
// is the one iOS gives us -- deviceMemory is Chromium-only, absent in exactly
// the browser with the problem, so it only serves as a second way in.
const MEMORY_CONSTRAINED =
  (navigator.deviceMemory !== undefined && navigator.deviceMemory <= 4) ||
  window.matchMedia("(max-width: 900px)").matches;

// Waits for the browser to have nothing better to do. The timeout is a
// backstop, not a target: a page that is never idle -- a pointer resting on a
// character keeps the yaw chasing, which keeps frames coming -- would otherwise
// never run the work at all.
function whenIdle(job, timeout = 2500) {
  // called with no arguments, deliberately: requestIdleCallback passes an
  // IdleDeadline, which a callback taking an index would silently use as one
  const run = () => job();
  if (typeof requestIdleCallback === "function") requestIdleCallback(run, { timeout });
  else setTimeout(run, 200);
}

// The phone breakpoint, matching mobile.css's own. Read per call rather than
// captured once, so rotating the device is picked up.
const PHONE_QUERY = "(max-width: 720px)";
const isPhone = () => window.matchMedia(PHONE_QUERY).matches;

const CORNERS = [
  "borderTopLeftRadius",
  "borderBottomLeftRadius",
  "borderTopRightRadius",
  "borderBottomRightRadius",
];

// Which side the panel's big sweep is on is a stylesheet decision: the rail
// sits on the left on desktop, and on the right on a phone, where it is the
// one thing here you actually operate and a thumb has to reach it. So the
// corners are given their roles by reading the resting panel rather than being
// named -- flip the panel in CSS and the morph mirrors itself.
//
//  - the top corner with the radius grows INTO the panel's signature sweep and
//    is carried through the whole move, because it is the one the eye follows;
//  - the bottom corner beneath it simply rides along, rounding that whole edge
//    with it and never straightening -- which it gets away with only because
//    the panel's bottom overshoots the viewport (see PANEL_BOTTOM_OVERSHOOT);
//  - the far pair resolve to a square 0, and hold the card's own radius until
//    the box has essentially landed. Straightening early reads as the panel
//    losing its shape before it arrives.
function cornerRoles(target) {
  const onLeft =
    (parseFloat(target.borderTopLeftRadius) || 0) >=
    (parseFloat(target.borderTopRightRadius) || 0);
  return onLeft
    ? {
        growing: ["borderTopLeftRadius"],
        joining: "borderBottomLeftRadius",
        late: ["borderTopRightRadius", "borderBottomRightRadius"],
      }
    : {
        growing: ["borderTopRightRadius"],
        joining: "borderBottomRightRadius",
        late: ["borderTopLeftRadius", "borderBottomLeftRadius"],
      };
}

// where along the flight the corners that end square start squaring off
const CORNER_SPLIT = 0.66;

// computed corner radii in px, so the card's own rounding can morph into the
// detail panel's single huge top-left corner
function readCorners(el) {
  const style = getComputedStyle(el);
  return Object.fromEntries(CORNERS.map((corner) => [corner, style[corner]]));
}

const lerpRect = (a, b, t) => ({
  left: lerp(a.left, b.left, t),
  top: lerp(a.top, b.top, t),
  width: lerp(a.width, b.width, t),
  height: lerp(a.height, b.height, t),
});

// smallest box containing both, so the character never leaves the canvas at
// any point along the flight and the canvas itself need never be resized
function unionRect(a, b) {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  return {
    left,
    top,
    width: Math.max(a.left + a.width, b.left + b.width) - left,
    height: Math.max(a.top + a.height, b.top + b.height) - top,
  };
}

// When a box travelling from `start` to `dest` has `dest` covered, as linear
// progress along the tween -- for deciding how long something sitting at `dest`
// has to stay hidden. Only the edges that begin INSIDE the destination can
// leave it showing, so those are the ones measured; a start box that already
// contains the destination answers 0.
//
// The curve is walked rather than inverted in closed form so that changing the
// ease cannot silently leave this answering for the old one.
function coverTime(start, dest, ease, slop) {
  const gap = Math.max(
    0,
    start.left - dest.left,
    start.top - dest.top,
    dest.right - start.right,
    dest.bottom - start.bottom
  );
  if (gap <= slop) return 0;
  const covered = 1 - slop / gap;
  const eased = gsap.parseEase(ease);
  for (let t = 0; t < 1; t += 1 / 240) {
    if (eased(t) >= covered) return t;
  }
  return 1;
}

function fadeIn(el, delay = 0) {
  el.classList.add("fade-scale-pane", "fade-scale-enter");
  requestAnimationFrame(() => {
    setTimeout(() => el.classList.remove("fade-scale-enter"), delay);
  });
}

/* ------------------------------------------------------------------ */
/* Grid view                                                          */
/* ------------------------------------------------------------------ */

function buildCharacterCard(character) {
  const card = document.createElement("button");
  card.className = "char-card";
  card.type = "button";
  card.setAttribute("aria-label", `${character.name} — ${character.movie}`);

  const canvasWrap = document.createElement("div");
  canvasWrap.className = "char-card-canvas-wrap";
  // horizontal nudge for poses that should spill off the card (Rosetta's gown)
  canvasWrap.style.setProperty("--shift-x", `${character.shiftX ?? 0}%`);
  canvasWrap.style.setProperty("--card-nudge", `${character.cardNudge ?? 0}px`);
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  // On the canvas, not the wrap. The filter that reads these lives on the
  // canvas, and the flight lifts that canvas out to <body> -- so set on the
  // wrap they simply stop inheriting the moment the character takes off, and
  // var(--aura, transparent) falls back to transparent. The detail view
  // already sets them this way; this makes the pair agree.
  canvas.style.setProperty("--aura", rgba(character.gradient[2], AURA.alpha));
  canvasWrap.appendChild(canvas);
  card.appendChild(canvasWrap);

  const panel = document.createElement("div");
  panel.className = "char-card-panel";
  paintPanel(panel, character);
  panel.innerHTML = `
    <h3 class="char-card-title" style="color:${character.titleTint}">${character.name}</h3>
    <p class="char-card-movie">Movie <b>${character.movie}</b></p>
  `;
  card.appendChild(panel);

  card.addEventListener("click", () => showDetail(character.id, canvas));

  // The viewer is NOT created here. Each character's model is 15-27MB of
  // geometry, and building all six up front meant the whole 112MB had to
  // arrive before the first card could draw. mount() is called by
  // buildGridView: immediately for the cards on screen, then in the
  // background for the rest. Idempotent, so the scroll handler and the
  // prefetch can both reach for it without coordinating.
  let viewer = null;
  // returns true only on the call that actually creates the viewer, so a
  // caller can count how many mounts it is waiting on
  const mount = (onSettled) => {
    // a deferred mount can outlive its grid -- if the view has been torn
    // down, the canvas is detached and there is nothing to draw into
    if (viewer || !canvas.isConnected) return false;
    // Drawn or failed, nothing is still waiting on this card. Both ends are
    // reported, because a model that cannot load would otherwise hold up the
    // prefetch queued behind it and the loading gate held in front of it --
    // and the page is perfectly usable with one canvas left blank.
    const settle = () => {
      modelSettled(character.model);
      onSettled?.();
    };
    viewer = createViewer(canvas, {
      modelUrl: character.model,
      ...cardFraming(character),
      yaw: ((character.yawDeg ?? 0) * Math.PI) / 180,
      rimColor: character.rimColor ?? character.gradient[0],
      spinSpeed: 0,
      onReady: settle,
      onError: settle,
    });
    renderLoop.add(viewer);
    state.viewers.push(viewer);
    return true;
  };

  // Hands the character back. The canvas keeps whatever it last drew -- it is
  // a 2D surface with pixels in it, independent of the viewer that filled it --
  // so a released card still looks right until it is redrawn, which matters
  // because releasing happens while the card is a long way off screen and a
  // wipe would be visible on the way back.
  const unmount = () => {
    if (!viewer) return false;
    renderLoop.remove(viewer); // disposes it, which gives up its claim
    state.viewers = state.viewers.filter((v) => v !== viewer);
    viewer = null;
    return releaseModel(character.model);
  };

  return { card, mount, unmount };
}

function buildGridView() {
  const view = document.createElement("div");
  view.className = "grid-view";

  const viewport = document.createElement("div");
  viewport.className = "carousel-viewport";
  const track = document.createElement("div");
  track.className = "carousel-track";
  const mounts = characters.map((character) => {
    const { card, mount, unmount } = buildCharacterCard(character);
    track.appendChild(card);
    return {
      id: character.id,
      model: character.model,
      card,
      mount,
      unmount,
      canvas: card.querySelector("canvas"),
      panel: card.querySelector(".char-card-panel"),
    };
  });
  // how the reverse flight finds the card it is flying back into
  view.cardFor = (id) => mounts.find((m) => m.id === id) ?? null;
  view.otherCards = (id) => mounts.filter((m) => m.id !== id).map((m) => m.card);

  // The row dealt in from the right as the page arrives from behind the
  // loading sheet -- see onPageIn at the bottom of this file. Called on the
  // first grid only: every later one either flies back out of the detail view
  // or was never away.
  //
  // Not applied when the view is built, even though that is where a "from"
  // state belongs: a transform changes what getBoundingClientRect reports, and
  // mountNearby uses exactly that to decide which characters to load. Held off
  // a card three hundred pixels to its right and the wrong four get mounted.
  view.dealIn = () => {
    // nothing to play back to someone who asked not to be moved -- the cards
    // are already sitting where this would land them
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const reach = (mounts[0]?.card.offsetWidth ?? 0) * CARD_ENTER_REACH;
    if (!reach) return;
    const cards = mounts.map(({ card }) => card);
    gsap.fromTo(
      cards,
      { x: reach, scale: CARD_ENTER_SCALE },
      {
        x: 0,
        scale: 1,
        duration: CARD_ENTER_SECONDS,
        ease: CARD_ENTER_EASE,
        stagger: CARD_ENTER_STAGGER,
        // handed back to its own styles the moment it lands, rather than left
        // under an inline transform for the flight to the detail view to
        // inherit and have to unpick
        clearProps: "transform",
      }
    );
    // same schedule, its own curve -- see CARD_ENTER_FADE_EASE
    gsap.fromTo(
      cards,
      { opacity: 0 },
      {
        opacity: 1,
        duration: CARD_ENTER_SECONDS * CARD_ENTER_FADE,
        ease: CARD_ENTER_FADE_EASE,
        stagger: CARD_ENTER_STAGGER,
        clearProps: "opacity",
      }
    );
    // how long the whole row takes to land, so the loading gate knows not to
    // start anything heavy until it has -- see onPageIn
    return CARD_ENTER_SECONDS + (cards.length - 1) * CARD_ENTER_STAGGER;
  };

  // Every model starts downloading now, on-screen or not. Only the per-viewer
  // work is deferred (see mountNearby). Deferring the fetch as well was a
  // mistake: on a throttled 20Mbit connection, paging to the second screenful
  // the moment the first had drawn left those two cards blank for eleven
  // seconds, because their bytes had not even been asked for yet. The bytes
  // are the part that has to be in flight early; the decode is the part that
  // has to wait its turn.
  for (const character of characters) {
    // a prefetch failure must not surface as an unhandled rejection: the card
    // that needs it will report it properly through onError when it mounts
    prefetchModel(character.model).catch(() => {});
  }
  viewport.appendChild(track);
  view.appendChild(viewport);

  const footer = document.createElement("footer");
  footer.className = "grid-footer";
  footer.innerHTML = `
    <!-- The two credits. The design points at the shot this was built from rather than at a
         profile or a portfolio home -- it is the specific thing being
         credited, and it is what someone following the link came to see. -->
    <div class="social-links">
      <!-- The "this is fan work" notice, folded into a mark rather than a line
           of small print: the disclaimer has to be findable, but it is not
           what the footer is for. It leads the row rather than trailing it --
           a notice about the whole page reads as a prefix to the credits, not
           as a third credit.

           A <button> and not a bare icon, so it is reachable by keyboard and
           announced. aria-describedby ties it to the text, which is a real
           element rather than a title attribute: those cannot be styled, take
           a second to appear, and do not exist on touch at all. -->
      <button
        class="notice"
        type="button"
        aria-label="About this project"
        aria-describedby="noticeText"
      >
        <!-- A heart, and not a copyright mark. Rendered beside
             the two credits, a (c) was the heaviest thing in the row and
             read as a legal notice stamped onto a page about cartoon
             characters. A heart carries the same weight as the
             text next to it, so the row stays quiet -- and "made with care,
             here is the fine print" is the honest tone for what this is.

             Solid, and sized against the 15px text rather than smaller than
             it. An outline at 17px was reading as a faint scratch beside two
             solid words -- a filled shape holds the same visual weight as the
             letterforms it sits with, which is what actually makes it blend
             rather than being literally lighter than everything else. -->
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 20.4s-7.4-4.7-7.4-9.8A4.2 4.2 0 0 1 12 7.7a4.2 4.2 0 0 1 7.4 2.9c0 5.1-7.4 9.8-7.4 9.8Z"
            fill="currentColor"
          />
        </svg>
        <span class="notice-tip" id="noticeText" role="tooltip"
          >A personal project, not affiliated with Disney</span
        >
      </button>
      <a href="https://github.com/kadetXx/disney-characters" target="_blank" rel="noopener noreferrer">Code</a>
      <a
        href="https://dribbble.com/shots/5874646-Disney-Characters-Web-UI"
        target="_blank"
        rel="noopener noreferrer"
      >Design</a>
    </div>
    <div class="pager">
      <button class="pager-btn" id="prevBtn" type="button" aria-label="Previous">
        <!-- the arms run into a curved apex rather than meeting at a point:
             stroke-linejoin:round only rounds by half the stroke width, which
             at 2px is a 1px corner and still reads as a hard V -->
        <svg width="9" height="15" viewBox="0 0 9 15" fill="none" aria-hidden="true"><path d="M7.5 1.5L3.4 6Q1.5 7.5 3.4 9L7.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        Prev
      </button>
      <button class="pager-btn" id="nextBtn" type="button" aria-label="Next">
        Next
        <svg width="9" height="15" viewBox="0 0 9 15" fill="none" aria-hidden="true"><path d="M1.5 1.5L5.6 6Q7.5 7.5 5.6 9L1.5 13.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
  view.appendChild(footer);

  const prevBtn = footer.querySelector("#prevBtn");
  const nextBtn = footer.querySelector("#nextBtn");

  // The carousel moves in whole pages, never free-scrolls: you are either on
  // the first screenful or the second, and any swipe commits to the far end
  // rather than tracking the pointer. The track's overflow-x is hidden (see
  // style.css) so the browser never scrolls it natively -- every movement
  // below is a deliberate scrollTo, which is what makes it feel snappy
  // instead of drifting to rest wherever momentum ran out.
  const SLIDE_MS = 420;
  // how much wheel travel / finger distance counts as a committed swipe
  const WHEEL_THRESHOLD = 24;
  const SWIPE_THRESHOLD = 40;
  let slideUntil = 0;

  // One page per screenful of overflow, minimum two whenever there is
  // anything at all to scroll -- so the far end is always reachable in one
  // press even when the overflow is less than a full viewport wide.
  function pageOffsets() {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll < 1) return [0];
    const count = Math.max(2, Math.round(maxScroll / track.clientWidth) + 1);
    return Array.from({ length: count }, (_, i) => (i * maxScroll) / (count - 1));
  }

  function currentPage() {
    const offsets = pageOffsets();
    let best = 0;
    offsets.forEach((offset, i) => {
      if (Math.abs(offset - track.scrollLeft) < Math.abs(offsets[best] - track.scrollLeft)) best = i;
    });
    return best;
  }

  function goToPage(index) {
    const offsets = pageOffsets();
    const target = Math.min(offsets.length - 1, Math.max(0, index));
    slideUntil = performance.now() + SLIDE_MS;
    track.scrollTo({ left: offsets[target], behavior: "smooth" });
  }

  // A swipe is one discrete command. Ignoring input mid-slide stops a single
  // trackpad flick -- which fires dozens of wheel events -- from queuing up
  // several pages and skating past the one the user asked for.
  function step(direction) {
    if (performance.now() < slideUntil) return;
    goToPage(currentPage() + direction);
  }

  // The page a given card sits on: the first one whose screenful contains it
  // outright, and failing that the last, which is where a card past the end of
  // every page must be. With two pages that is simply "left side or right
  // side", which is all the carousel can express.
  function pageHolding(card) {
    const offsets = pageOffsets();
    const left = card.offsetLeft;
    const right = left + card.offsetWidth;
    for (let i = 0; i < offsets.length; i++) {
      if (left >= offsets[i] - 1 && right <= offsets[i] + track.clientWidth + 1) return i;
    }
    return offsets.length - 1;
  }

  // Jump (never slide) the carousel to wherever a character's card lives.
  // Used when returning from the detail view so the card is already on screen
  // -- and, for the reverse flight, so it can be measured before anything
  // moves. scroll-behavior is smooth in the stylesheet, so it has to be
  // overridden or this animates instead of applying at once.
  function revealCard(characterId) {
    const index = characters.findIndex((c) => c.id === characterId);
    const entry = mounts[index];
    if (!entry) return;
    const previous = track.style.scrollBehavior;
    track.style.scrollBehavior = "auto";
    track.scrollLeft = pageOffsets()[pageHolding(entry.card)] ?? 0;
    track.style.scrollBehavior = previous;
    syncPagerState();
    // The edge scale is a TRANSITIONED transform, and the reverse flight
    // measures the card it is flying into in the statement after this one. Left
    // alone, the wrap is still at its old scale in that frame and eases to the
    // resting one over the next 260ms -- so the flight frames the character for
    // a box 8% larger than the card ends up, carries it the whole way at that
    // size, and drops it on landing.
    //
    // So the transition is suppressed for the one frame that applies the scale,
    // exactly as scroll-behavior is overridden above so the jump is a jump. On
    // desktop this changes nothing: the card being returned to is already at
    // full size, so there was never a transform to transition.
    const wraps = [...track.querySelectorAll(".char-card-canvas-wrap")];
    for (const wrap of wraps) wrap.style.transition = "none";
    syncEdgeScale();
    // commit the transformed box before the transition is handed back, or
    // restoring it here would simply start the animation we just skipped
    void track.offsetWidth;
    for (const wrap of wraps) wrap.style.transition = "";
  }
  view.revealCard = revealCard;

  track.addEventListener(
    "wheel",
    (event) => {
      // trackpads report horizontal intent on deltaX; a mouse wheel only has
      // deltaY, so take whichever axis the gesture actually favours
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < WHEEL_THRESHOLD) return;
      event.preventDefault();
      step(Math.sign(delta));
    },
    { passive: false }
  );

  let touchStartX = null;
  track.addEventListener(
    "touchstart",
    (event) => {
      touchStartX = event.touches[0].clientX;
    },
    { passive: true }
  );
  track.addEventListener(
    "touchend",
    (event) => {
      if (touchStartX === null) return;
      const dx = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(dx) >= SWIPE_THRESHOLD) step(dx < 0 ? 1 : -1);
    },
    { passive: true }
  );

  // Items shrink as they approach and cross the scrollport edge, and return to
  // full size coming back in. The falloff spans two cards at each edge: the
  // outermost reaches EDGE_SCALE_MIN, the one inside it sits about halfway.
  //
  // Measured from layout geometry (offsetWidth / scrollLeft), never from
  // getBoundingClientRect -- that reports the *transformed* box, so feeding it
  // back in would make each card's scale depend on its own previous scale.
  const EDGE_SCALE_MIN = 0.74;
  // Where along the x axis shrinking starts and finishes, in card-pitch units
  // measured from the scrollport edge. Negative starts it while the card is
  // still inside the port; 0 would hold full size right up until the outer
  // edge touches the screen edge. Both boundaries move together so the ramp
  // keeps its length -- only the point it engages shifts.
  const EDGE_TRIGGER = -0.5;
  const EDGE_FULL = 0.7;
  let edgeFrame = null;
  function syncEdgeScale() {
    edgeFrame = null;
    const cards = [...track.children];
    if (!cards.length) return;
    const cs = getComputedStyle(track);
    const gap = parseFloat(cs.columnGap) || 0;
    const pad = parseFloat(cs.paddingLeft) || 0;
    const cardW = cards[0].offsetWidth;
    const pitch = cardW + gap;
    const viewLeft = track.scrollLeft;
    const viewRight = viewLeft + track.clientWidth;

    // Each edge only exerts pull while there is still content to scroll past
    // it, eased over one card so it fades in rather than snapping. Without
    // this the first card shrinks while parked at the start, where nothing is
    // actually leaving the viewport.
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const leftPull = Math.min(1, viewLeft / pitch);
    const rightPull = Math.min(1, (maxScroll - viewLeft) / pitch);
    const ramp = (over) =>
      Math.min(1, Math.max(0, (over - EDGE_TRIGGER) / (EDGE_FULL - EDGE_TRIGGER)));

    cards.forEach((card, i) => {
      const left = pad + i * pitch;
      const right = left + cardW;
      // in card-pitch units: -1 is a full card inside the edge, 0 straddles
      // it, +1 is a full card beyond it
      const t = Math.max(
        ramp((viewLeft - left) / pitch) * leftPull,
        ramp((right - viewRight) / pitch) * rightPull
      );
      const scale = (1 - t * (1 - EDGE_SCALE_MIN)).toFixed(4);
      card.querySelector(".char-card-canvas-wrap").style.transform = `scale(${scale})`;
    });
  }
  function queueEdgeScale() {
    if (edgeFrame === null) edgeFrame = requestAnimationFrame(syncEdgeScale);
  }

  function syncPagerState() {
    const page = currentPage();
    prevBtn.disabled = page === 0;
    nextBtn.disabled = page === pageOffsets().length - 1;
  }

  // Everything not already mounted, fetched in the background. This is what
  // keeps deferral from ever showing an empty card: the off-screen models are
  // on their way while the user is still looking at the first page, not when
  // they ask for the second. One at a time, so several multi-megabyte decodes
  // cannot pile onto each other.
  let prefetching = false;
  function prefetchRest(index = 0) {
    if (index >= mounts.length) return;
    const { card, mount } = mounts[index];
    if (!card.isConnected) return; // grid torn down; stop walking
    // Wait for each one to finish before starting the following one, AND let
    // the browser back in between them. Each mount is a synchronous block of
    // its own that nothing can interrupt, so the only thing that can be done
    // about a run of them is to refuse to run them back to back: two seconds
    // frozen, a chance to respond, two more, beats four seconds frozen.
    const next = () => whenIdle(() => prefetchRest(index + 1));
    if (!mount(next)) next();
  }
  function startPrefetch() {
    if (prefetching) return;
    prefetching = true;
    // Not on a phone, at any point. Mounting the rest is a convenience -- it
    // saves a beat when paging -- and it is not worth a killed tab. mountNearby
    // still builds each card as it is reached.
    if (MEMORY_CONSTRAINED) return;
    // Never underneath the loading sheet, and never in the beat right after it
    // either.
    //
    // Bringing a character on screen costs the main thread seconds at a time --
    // profiled at 6x CPU throttle, this work froze the page for 5.1s starting
    // 1.2s after the reveal, and 41.6% of that window was the stage's readback
    // blit. So the entrance ran at a clean 17ms median and then the finished
    // page locked up, which is worse: by then it looks ready and invites a
    // click.
    //
    // requestIdleCallback cannot interrupt a synchronous block, so this is not
    // about making the work cheaper -- it is about starting it when the browser
    // is genuinely doing nothing, rather than on a fixed timer that lands in
    // the middle of someone reaching for a card. Nothing waits on it: these are
    // second-page cards, and paging there runs mountNearby, which mounts what
    // is actually needed regardless of whether this has run yet.
    afterEntrance(() => whenIdle(prefetchRest));
  }

  // Cards whose model should already be in hand: anything on screen, plus half
  // a screen of runway either side, so a card is never mounted at the exact
  // moment it becomes visible.
  //
  // The prefetch deliberately waits until these have actually DRAWN rather
  // than merely been asked for. The models are 15-27MB of geometry each and
  // the cost is decode and GPU upload, not download -- measured, every
  // file had arrived by 4.3s but nothing was on screen until 12.8s. Starting
  // all six decoding at once simply made the visible ones queue behind the
  // ones nobody can see.
  let waitingOn = 0;
  let gatedEntrance = false;
  function mountNearby() {
    // half a card of runway. Measured against the card, not the scrollport:
    // half a viewport is wider than the entire overflow here, so it swept in
    // all six and the deferral did nothing at all.
    // Half a card of runway on a desktop, where four are visible at once and
    // the next is already half on screen. A full card on a phone, where the
    // carousel's pitch is a whole screen: half a card there means the next
    // character only starts parsing as it arrives, and an ~85MB parse is about
    // a second of blank card. Mounting one ahead is the difference between
    // sliding onto a drawn card and sliding onto an empty one.
    const pad = (mounts[0]?.card.offsetWidth ?? 0) * (MEMORY_CONSTRAINED ? 1.05 : 0.5);
    const nearby = mounts.filter(({ card }) => {
      const r = card.getBoundingClientRect();
      return r.right > -pad && r.left < window.innerWidth + pad;
    });
    // What the loading sheet is held up by: every card of this first screenful,
    // which is four across a desktop and one on a phone. Registered BEFORE the
    // mounts below, so a model already in cache cannot report itself drawn
    // before the sheet knows it was waiting for it. Only the first call counts
    // -- see gateOn -- so a card mounted later by scrolling never re-arms it.
    if (!gatedEntrance) {
      gatedEntrance = true;
      gateOn(nearby.map(({ model }) => model));
    }
    for (const { mount } of nearby) {
      if (mount(() => { if (--waitingOn <= 0) startPrefetch(); })) waitingOn++;
    }
    releaseDistant(nearby);
  }

  // How far past the mounted window a card gets before its character is handed
  // back, in cards.
  //
  // Tight, because the budget is tight. At 85-126MB decoded per character, a
  // phone holds about two before iOS starts killing the tab -- measured at 2.5
  // cards of slack the carousel peaked at four resident, ~413MB, and Safari
  // still died two slides in. Keeping the current card and the one being
  // mounted ahead of it is roughly 200MB, which is the most this can spend.
  //
  // The cost of being wrong in this direction is a re-parse when you come back;
  // the cost of being wrong in the other is a crash.
  const RELEASE_AFTER_CARDS = 0.25;

  // Only on the devices that cannot hold everything. A desktop keeps every
  // character it has built, which is what makes paging back instant there.
  function releaseDistant(nearby) {
    if (!MEMORY_CONSTRAINED) return;
    const width = mounts[0]?.card.offsetWidth ?? 0;
    if (!width) return;
    const keep = new Set(nearby);
    const reach = width * RELEASE_AFTER_CARDS;
    for (const entry of mounts) {
      if (keep.has(entry)) continue;
      const r = entry.card.getBoundingClientRect();
      const past = r.right < -reach || r.left > window.innerWidth + reach;
      // the character currently expanded is being drawn by the detail view's
      // own viewer, which holds its own claim -- releaseModel will decline
      if (past) entry.unmount();
    }
  }

  prevBtn.addEventListener("click", () => step(-1));
  nextBtn.addEventListener("click", () => step(1));
  track.addEventListener(
    "scroll",
    () => { syncPagerState(); queueEdgeScale(); mountNearby(); },
    { passive: true }
  );
  window.addEventListener("resize", queueEdgeScale, { passive: true });
  requestAnimationFrame(() => {
    syncPagerState();
    syncEdgeScale();
    // on-screen cards first, so the first paint waits only on those; the rest
    // follow via startPrefetch once these have drawn
    mountNearby();
    // belt and braces: if a visible model never resolves, the off-screen ones
    // must still get fetched rather than waiting on it forever
    setTimeout(startPrefetch, PREFETCH_FALLBACK_MS);
  });

  return view;
}

/* ------------------------------------------------------------------ */
/* Detail view                                                        */
/* ------------------------------------------------------------------ */

// drop-shadow has no spread parameter the way box-shadow does, so the two
// levers are blur distance and colour alpha: push blur up and alpha down for a
// wider, softer halo; pull blur in and alpha up for a tighter, punchier one.
// The halo's radius lives in CSS as --aura-size, so it can vary by
// breakpoint; only its colour and strength are decided per character here.
const AURA = { alpha: 0.4 };

function rgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// The movie's logo, contained
// rather than cropped so a real logo of any sensible aspect drops straight in
// without being cut.
function buildBadge(character) {
  const badge = document.createElement("div");
  badge.className = "detail-badge";
  const movie = getMovie(character.movie);
  const logo = document.createElement("img");
  logo.className = "movie-logo";
  logo.src = movie.logo;
  logo.style.setProperty("--logo-scale", movie.logoScale ?? 1);
  // decorative: the movie is already named in the text beside it
  logo.alt = "";
  badge.appendChild(logo);
  return badge;
}

// Split out of buildDetailView so switching characters can rebuild just this
// block and cross-fade it, instead of rebuilding the whole view.
function buildDetailInfo(character) {
  const info = document.createElement("div");
  info.className = "detail-info";
  info.innerHTML = `
    <div class="detail-text">
      <h2 class="detail-title" style="color:${character.titleTint}">${character.name}</h2>
      <p class="detail-movie">Movie <b>${character.movie}</b></p>
      <p class="detail-description">${character.description}</p>
    </div>
  `;
  info.appendChild(buildBadge(character));
  return info;
}

// The description wraps to whatever width the panel gives it, and during a
// flight the panel is still growing -- so the text re-wraps under itself as it
// fades in. Measured at 1180x820 on Disgust: eight lines at the frame it
// becomes visible, seven by the next, a 30px jump in the middle of the fade.
// At 1440 the last re-wrap happens to land one frame BEFORE it appears, which
// is why it looked fine at some sizes and not others.
//
// Pinned to the width it will finish at, taken while the view is still laid
// out at full size and before the panel is pinned for the flight. The line
// count is then decided once, and nothing it does afterwards can change it.
// Cleared on landing so the stylesheet's own sizing resumes and a resize
// behaves normally.
function pinTextWidth(view) {
  const text = view?.querySelector(".detail-text");
  if (!text) return null;
  const width = text.getBoundingClientRect().width;
  if (!width) return null;
  // width alone is not enough: .detail-info is a flex row and this is a flex
  // item, so the default flex-shrink lets it fall below whatever width it is
  // given the moment the panel is narrower than the content. Measured setting
  // width to 311px and reading 189px back. min-width and flex-shrink together
  // are what actually hold it.
  Object.assign(text.style, {
    width: `${width}px`,
    minWidth: `${width}px`,
    flexShrink: "0",
  });
  // removed directly rather than through gsap's clearProps, which takes CSS
  // names and was quietly leaving all three in place
  return () => {
    text.style.removeProperty("width");
    text.style.removeProperty("min-width");
    text.style.removeProperty("flex-shrink");
  };
}

// The character's canvas, and the viewer that draws into it. Also split out so
// a switch can build a second one and slide it in over the first.
function buildMascotCanvas(character) {
  const canvas = document.createElement("canvas");
  canvas.className = "detail-mascot-canvas";
  canvas.setAttribute("aria-hidden", "true");
  canvas.style.setProperty("--detail-shift-x", `${character.detailShiftX ?? 0}%`);
  canvas.style.setProperty("--detail-shift-y", `${character.detailShiftY ?? 0}%`);
  canvas.style.setProperty("--mobile-nudge", `${character.mobileNudge ?? 0}px`);
  // the same accent halo the cards carry, and the same one the flight between
  // them carries. Set on the canvas rather than on any wrapper because the
  // flight lifts this element out to <body>, and an inherited custom property
  // would stop resolving the moment it did.
  canvas.style.setProperty("--aura", rgba(character.gradient[2], AURA.alpha));
  return canvas;
}

function mountMascot(canvas, character, onPainted) {
  const viewer = createViewer(canvas, {
    modelUrl: character.model,
    ...detailFraming(character),
    yaw: ((character.yawDeg ?? 0) * Math.PI) / 180,
    rimColor: character.rimColor ?? character.gradient[0],
    spinSpeed: 0,
    onReady: () => {
      viewer.render();
      onPainted?.();
    },
  });
  renderLoop.add(viewer);
  state.viewers.push(viewer);
  return viewer;
}

function buildClips(character) {
  const clips = document.createElement("div");
  clips.className = "detail-clips";
  const label = document.createElement("span");
  label.className = "detail-clips-label";
  label.textContent = "Clips";
  clips.appendChild(label);

  const track = document.createElement("div");
  track.className = "clips-track";

  getMovie(character.movie).clips.forEach((clip, i) => {
    // A div, not a button: once it is playing it contains an iframe, and a
    // button wrapping an interactive player swallows the player's own controls
    // (and is invalid). It carries the button role only while it is still a
    // poster waiting to be clicked -- stopClip puts that back.
    const card = document.createElement("div");
    card.className = "clip-card";
    card.innerHTML = `
      <img class="clip-poster" src="${clip.poster}" alt="" loading="lazy">
      <span class="clip-play">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M10 8.5L16 12L10 15.5V8.5Z" fill="#243A74"/></svg>
      </span>
    `;

    // A video with no maxres thumbnail comes back as a 120x90 grey placeholder
    // on a 404, which loads successfully as far as the browser is concerned --
    // so both the error and the too-small case have to be caught.
    const poster = card.querySelector(".clip-poster");
    const useFallback = () => {
      if (poster.dataset.fallback) return;
      poster.dataset.fallback = "1";
      poster.src = clip.posterFallback;
    };
    poster.addEventListener("error", useFallback);
    poster.addEventListener("load", () => {
      if (poster.naturalWidth < 320) useFallback();
    });

    const asPoster = () => {
      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");
      card.setAttribute("aria-label", `Play ${character.movie} trailer ${i + 1}`);
    };
    asPoster();

    const stop = () => {
      card.classList.remove("is-playing");
      // Removing the iframe is what actually stops it -- there is no pause()
      // to reach for without loading YouTube's player API, and leaving it in
      // the DOM leaves the audio running. It costs nothing to rebuild: the
      // next click makes a fresh one.
      card.querySelector(".clip-embed")?.remove();
      asPoster();
    };

    const play = () => {
      if (card.classList.contains("is-playing")) return;
      // only ever one at a time, so starting a second does not leave the first
      // playing behind it
      for (const other of track.querySelectorAll(".clip-card.is-playing")) {
        other.dispatchEvent(new CustomEvent("clip:stop"));
      }
      card.classList.add("is-playing");
      // it is the player's turn to handle keys and clicks now
      card.removeAttribute("role");
      card.removeAttribute("tabindex");
      card.removeAttribute("aria-label");

      // Built on click rather than up front: eighteen embeds across six
      // characters, nearly all of them never played, and each one is a whole
      // document. The poster is what gets seen until one is asked for.
      // autoplay is allowed here because the click IS the user gesture.
      const frame = document.createElement("iframe");
      frame.className = "clip-embed";
      frame.title = `${character.movie} trailer ${i + 1}`;
      frame.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      frame.allowFullscreen = true;
      frame.referrerPolicy = "strict-origin-when-cross-origin";
      // -nocookie so idle viewers who never press play are not tracked
      frame.src =
        `https://www.youtube-nocookie.com/embed/${clip.youtubeId}` +
        `?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      card.appendChild(frame);
    };

    card.addEventListener("clip:stop", stop);
    card.addEventListener("click", () => {
      if (!card.classList.contains("is-playing")) play();
    });
    card.addEventListener("keydown", (event) => {
      if (card.classList.contains("is-playing")) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        play();
      }
    });
    track.appendChild(card);
  });

  clips.appendChild(track);
  return clips;
}

let mainViewer = null;
// set when a detail view is built mid-flight; run once the character lands
let pendingMascot = null;

// onPainted runs only once the mascot canvas actually has the character drawn
// into it -- see the mount closure in buildDetailView
function mountMascotViewer(onPainted) {
  const mount = pendingMascot;
  pendingMascot = null;
  mount?.(onPainted);
}

// Swiping up and down switches characters -- on TOUCH ONLY. Deliberately no
// wheel handler: with a mouse, switching stays on the chevron, so changing
// character means travelling across the screen to click it, and that travel is
// what makes the character's cursor-follow read as alive. A wheel flick
// switches without moving the pointer at all, which loses that and invites
// spinning through six characters at a redraw apiece.
//
// No feature detection needed for the split -- touch events simply do not fire
// for a mouse or trackpad, so binding these alone IS the condition.
//
// The gesture follows the carousel's model: one swipe is one discrete command
// rather than a drag that tracks the finger, and input is ignored until the
// swap it started has finished, so a fast repeat swipe cannot skate several
// characters past the one asked for. Threshold matches the carousel's, so both
// need the same amount of intent.
const DETAIL_SWIPE_THRESHOLD = 40;

function attachDetailSwipe(view) {
  let busyUntil = 0;

  // up goes forward, so finger and content travel together; down goes back,
  // with the whole transition mirrored
  const step = (direction) => {
    if (performance.now() < busyUntil) return;
    const index = characters.findIndex((c) => c.id === state.detailId);
    if (index < 0) return;
    const target = characters[(index + direction + characters.length) % characters.length];
    // read at call time: SWITCH_SECONDS is declared further down the file
    busyUntil = performance.now() + SWITCH_SECONDS * 1000;
    showDetail(target.id, null, direction);
  };

  let startX = null;
  let startY = null;
  view.addEventListener(
    "touchstart",
    (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    },
    { passive: true }
  );
  view.addEventListener(
    "touchend",
    (event) => {
      if (startY === null) return;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      startX = startY = null;
      // vertical intent only, so swiping the clip row sideways still scrolls it
      if (Math.abs(dy) <= Math.abs(dx)) return;
      if (Math.abs(dy) < DETAIL_SWIPE_THRESHOLD) return;
      step(dy < 0 ? 1 : -1);
    },
    { passive: true }
  );
}

// Turning the character with a finger.
//
// The cursor version follows the pointer wherever it is on the page, which a
// finger cannot do: it is only on the glass while it is dragging. So the same
// mapping -- YAW_SPAN of travel is worth YAW_SWING of turn -- is driven by the
// distance dragged instead, carried on from wherever the last drag ended.
//
// Delegated from the view rather than bound to the canvas, for two reasons:
// the canvas is pointer-events:none and its overscanned box lies across the
// panel's copy anyway, so it is not a target you could aim at; and the canvas
// is replaced outright on every character switch, which would strand a
// listener bound to it.
//
// The clip row and the controls are excluded. The row is a horizontal
// scroller, so a sideways drag there is a scroll and nothing else.
const YAW_DRAG_IGNORE = ".detail-clips, .detail-rail, a, button";

function attachMascotDrag(view) {
  let dragging = false;
  let startX = 0;
  let startHeld = 0;

  view.addEventListener("pointerdown", (event) => {
    if (event.pointerType !== "touch") return;
    if (event.target.closest?.(YAW_DRAG_IGNORE)) return;
    dragging = true;
    startX = event.clientX;
    startHeld = yawHeld;
  });

  view.addEventListener(
    "pointermove",
    (event) => {
      if (!dragging || event.pointerType !== "touch") return;
      const canvas = view.querySelector(".detail-mascot-canvas");
      const viewer = state.viewers.find((v) => v.canvas === canvas);
      if (!viewer) return;
      const span = Math.max(1, window.innerWidth * YAW_SPAN);
      // the viewer clamps to its own limit, which YAW_SWING already sits at,
      // so dragging past the end simply stops turning her
      yawHeld = startHeld + ((event.clientX - startX) / span) * YAW_SWING;
      viewer.setYawOffset(yawHeld);
    },
    { passive: true }
  );

  const end = () => {
    dragging = false;
  };
  view.addEventListener("pointerup", end);
  view.addEventListener("pointercancel", end);
}

function buildDetailView(characterId, { deferMascot = false } = {}) {
  const character = getCharacter(characterId);
  const nextCharacter = characters[(characters.findIndex((c) => c.id === characterId) + 1) % characters.length];

  const view = document.createElement("div");
  view.className = "detail-view";

  const closeRow = document.createElement("div");
  closeRow.className = "close-row";
  closeRow.innerHTML = `
    <button class="close-btn" type="button">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
      Close
    </button>
  `;
  closeRow.querySelector(".close-btn").addEventListener("click", showGrid);
  view.appendChild(closeRow);

  const body = document.createElement("div");
  body.className = "detail-body";

  const rail = document.createElement("div");
  rail.className = "detail-rail";
  rail.innerHTML = `
    <span class="rail-tab is-active">${character.name}</span>
    <div class="rail-next-group">
      <button class="rail-tab" type="button">${nextCharacter.name}</button>
      <button class="rail-chevron" type="button" aria-label="Next character">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
  const goNext = () => showDetail(nextCharacter.id);
  rail.querySelector(".rail-tab:not(.is-active)").addEventListener("click", goNext);
  rail.querySelector(".rail-chevron").addEventListener("click", goNext);
  body.appendChild(rail);

  const panel = document.createElement("div");
  panel.className = "detail-panel";
  paintPanel(panel, character);


  panel.appendChild(buildDetailInfo(character));
  panel.appendChild(buildClips(character));

  body.appendChild(panel);

  const canvas = buildMascotCanvas(character);
  body.appendChild(canvas);

  attachDetailSwipe(view);
  attachMascotDrag(view);

  view.appendChild(body);

  // Deferred during a flight: a second full-size viewer rendering the same
  // character while the flight is mid-air doubles the per-frame cost at
  // exactly the wrong moment. mountMascotViewer() is called on landing.
  const mount = (onPainted) => {
    // held locally rather than read back off mainViewer: onReady fires a
    // microtask later, by which point a fast second navigation may already
    // have pointed mainViewer at a different character's viewer
    const viewer = createViewer(canvas, {
      modelUrl: character.model,
      ...detailFraming(character),
      yaw: ((character.yawDeg ?? 0) * Math.PI) / 180,
      rimColor: character.rimColor ?? character.gradient[0],
      spinSpeed: 0,
      // onReady fires once the model is loaded and framed -- but nothing has
      // been DRAWN yet: the render loop is a separate rAF callback and would
      // not reach this viewer until the next frame. Draw synchronously here so
      // the canvas is never handed to the flight (or to the user) blank.
      onReady: () => {
        viewer.render();
        onPainted?.();
      },
    });
    mainViewer = viewer;
    renderLoop.add(viewer);
    state.viewers.push(viewer);
  };

  if (deferMascot) pendingMascot = mount;
  else mount();

  return view;
}

/* ------------------------------------------------------------------ */
/* View switching                                                     */
/* ------------------------------------------------------------------ */

// Cards still playing their exit animation. They live on <body> rather than in
// #app, so nothing that rebuilds #app can clear them -- this list is how they
// get collected if the user navigates again mid-exit.
let outgoingCards = [];

function dropOutgoingCards() {
  for (const card of outgoingCards) card.remove();
  outgoingCards = [];
}

// fade:false is for the reverse flight, which measures rects inside the view it
// has just mounted -- fadeIn enters from scale(0.96), so anything measured
// under it is 4% short and the flight lands in the wrong place.
function mount(buildFn, { fade = true } = {}) {
  dropOutgoingCards();
  clearViewers();
  app.innerHTML = "";
  const view = buildFn();
  app.appendChild(view);
  if (fade) fadeIn(view);
  return view;
}

// When the grid arrives behind the shrinking panel, as a share of the flight.
// Late on purpose: the shrinking panel does not cover the destination card for
// most of the move -- at the halfway point its left edge is still well right of
// a card sitting at the start of the row -- so bringing the grid in early let
// you see the card being flown into before the flight got there. The cards now
// land just as the panel does, and the character is the only thing to follow
// until then.
const CARD_RETURN_SECONDS = 0.3;
const CARD_RETURN_DELAY = 0.55;
// The curve the panel's box travels on, in both directions. Named because the
// reveal below has to solve against exactly the one the panel is using.
const PANEL_MORPH_EASE = "power3.inOut";
// 0.55 is late enough for five of the six. It is not late enough for Rosetta,
// and the difference is where her card rests rather than anything about the
// timing: the expanded panel's left edge sits at the rail, 144px to the RIGHT
// of the first card in the row, so that strip is the one piece of the grid the
// shrinking panel never covers until it arrives. Every other card's box is
// inside the panel's for the whole move, which is why nothing shows there.
//
// What it looked like: a pale vertical sliver of Rosetta's own card panel --
// the card at 70% over white, against the panel at full strength -- down the
// left edge of the panel for the last 200ms, reading as a flash of the
// destination just before the landing. 17px of it at t=0.65, 4.5px at 0.75.
//
// So the card being landed on no longer waits for a fixed moment but for
// cover: the progress at which the panel's box covers its box, give or take a
// pixel. A card the panel already contains solves to 0 and keeps the timing
// above, so this changes nothing for the other five.
const LANDING_COVER_SLOP = 1.5;
// and it is opaque a little before the panel is taken away, rather than on the
// same frame -- the hand-off should never depend on which tween GSAP happens
// to finish first
const LANDING_CARD_OPAQUE_BY = 0.96;
// however tight cover turns out to be, it never gets less of the flight than
// this to come up in -- a card appearing in one frame is the flash back again,
// just at the other end
const CARD_RETURN_MIN = 0.1;
// back.in read backwards, softened. The cards leave on CARD_EXIT_EASE, a
// spring that winds up before it goes, and returning on its curve makes the
// two halves one motion rather than an exit with a fade for a partner.
//
// Not the exact mirror, though, because the two ends can afford different
// amounts of spring. Leaving, the overshoot happens as the card is already
// fading and heading off the page. Arriving, it happens at the end, on a card
// that has landed and is fully opaque, where it reads as a bounce rather than
// as weight. back.out(2.2) peaks 15.4% past the target -- 13px above rest for
// the nearest card and 20px for the outermost, which drop further. This peaks
// at 7.1%: 5.9px and 9.3px. Chosen by feel on a device, from 2.2, 1.6, 1.4,
// 1.2 and 1.
const CARD_RETURN_EASE = "back.out(1.4)";
// The footer lands last of everything, a touch behind the cards it sits under.
const FOOTER_RETURN_DELAY = 0.92;
const FOOTER_RETURN_SECONDS = 0.5;
// the detail panel's own text goes early, while the box is still near full
// size; below about this the layout it was written for no longer fits
const DETAIL_TEXT_FADE = 0.28;

// When the rail and the close row arrive, as shares of the flight.
//
// They used to start at 0.12 and be fully in by 0.46, which put them on screen
// while the cards they replace were still dropping out of the row -- two
// unrelated things moving over each other. The cards are clear by roughly 0.5
// (CARD_EXIT_SECONDS plus its stagger), so these now begin after that and
// settle just past the landing: the panel arrives, then its furniture does.
const CHROME_FADE_DELAY = 0.8;
const CHROME_FADE = 0.5;

// The mirror of flyToDetail: carries the expanded character back into its card
// rather than cutting to the grid. Same machinery throughout -- the mascot's
// own canvas and viewer are adopted, the canvas is sized once to the union of
// both rects, and the character is moved by camera framing alone.
function flyToGrid(characterId) {
  const character = getCharacter(characterId);
  const detailView = app.firstElementChild;
  const mascotCanvas = detailView?.querySelector(".detail-mascot-canvas");
  const viewer = mascotCanvas && state.viewers.find((v) => v.canvas === mascotCanvas);
  if (!viewer) return false;

  const from = mascotCanvas.getBoundingClientRect();
  if (!from.width || !from.height) return false;

  // anything still in the air from the expand (its text ghost, the footer)
  dropOutgoingCards();

  // the panel is still at rest at this point, so this measures the same width
  // the text has been wrapping to all along
  pinTextWidth(detailView);
  const detailPanel = detailView.querySelector(".detail-panel");
  const panelFrom = detailPanel?.getBoundingClientRect();
  const panelFromCorners = detailPanel ? readCorners(detailPanel) : null;
  const detailContents = detailPanel ? [...detailPanel.children] : [];

  // hand the viewer to the flight before clearViewers() can dispose it
  state.viewers = state.viewers.filter((v) => v !== viewer);

  // Lift the two pieces that carry across out of the detail view before it is
  // torn down. Both are pinned to the box they already occupy, so nothing
  // moves in the frame the grid replaces the detail layout.
  if (detailPanel && panelFrom) {
    Object.assign(detailPanel.style, {
      position: "fixed",
      margin: "0",
      left: `${panelFrom.left}px`,
      top: `${panelFrom.top}px`,
      width: `${panelFrom.width}px`,
      height: `${panelFrom.height}px`,
      zIndex: "5",
      pointerEvents: "none",
      ...panelFromCorners,
    });
    document.body.appendChild(detailPanel);
    outgoingCards.push(detailPanel);
  }
  // the class has to come OFF, not just have .flight-canvas added alongside:
  // .detail-mascot-canvas is declared later in the stylesheet, so at equal
  // specificity its position:absolute and z-index:6 would beat the flight's
  // fixed/60 and the canvas would be laid out against a detail view that no
  // longer exists.
  mascotCanvas.classList.remove("detail-mascot-canvas");
  mascotCanvas.classList.add("flight-canvas");
  document.body.appendChild(mascotCanvas);

  // The close button and the rail's name labels go the same way -- pinned and
  // faded rather than deleted with the view. They fade in on the way out to
  // the detail layout, so cutting them on the way back was the one asymmetry
  // left in the pair.
  for (const el of [detailView.querySelector(".close-row"), detailView.querySelector(".detail-rail")]) {
    if (!el) continue;
    const box = el.getBoundingClientRect();
    Object.assign(el.style, {
      position: "fixed",
      margin: "0",
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      pointerEvents: "none",
    });
    document.body.appendChild(el);
    outgoingCards.push(el);
    gsap.to(el, {
      opacity: 0,
      duration: FLIGHT_SECONDS * DETAIL_TEXT_FADE,
      ease: "power2.in",
      onComplete: () => el.remove(),
    });
  }

  clearViewers();
  app.innerHTML = "";

  // Built without the usual fadeIn for the same reason the forward flight
  // skips it: everything below is measured inside this view.
  const view = buildGridView();
  app.appendChild(view);
  view.revealCard(characterId);

  const target = view.cardFor(characterId);
  if (!target) return false;
  const to = target.canvas.getBoundingClientRect();
  // the flying character stands in for this one until it lands
  target.canvas.style.visibility = "hidden";

  // The card's own viewer has to be building while the flight is in the air,
  // or the hand-off at the end has nothing to hand over to. Its model is
  // already cached, so this is the Box3 and the upload, not a download.
  // mount() is idempotent and ignores the callback once a viewer exists, so
  // the landing cannot just call it again and expect to be told -- it has to
  // leave word here instead.
  let cardReady = false;
  let whenCardReady = null;
  target.mount(() => {
    cardReady = true;
    whenCardReady?.();
  });

  // The cards come back the way they left, reversed: up from the drop, out of
  // the tilt, rippling outward from the one being flown into. Fading them in
  // on the spot was the whole of it before, which read as the row appearing
  // rather than returning -- a flash, especially against a panel still
  // visibly shrinking.
  //
  // The card being landed on is deliberately not part of it. It is the
  // destination, and the panel is morphing into its box; drop-and-tilting the
  // thing the character is arriving at would fight the landing.
  // The footer is part of the grid, so it is on screen the instant the grid is
  // built -- which is at the START of the flight back, under a panel that is
  // still nearly full size. It leaves on its own schedule going out; coming
  // back it now waits for the row it belongs to.
  const returningFooter = view.querySelector(".grid-footer");
  if (returningFooter) {
    gsap.fromTo(
      returningFooter,
      { opacity: 0 },
      {
        opacity: 1,
        duration: FLIGHT_SECONDS * FOOTER_RETURN_SECONDS,
        ease: "power2.out",
        delay: FLIGHT_SECONDS * FOOTER_RETURN_DELAY,
      }
    );
  }

  const targetLeft = target.card.getBoundingClientRect().left;
  const returning = view.otherCards(characterId);
  const returnRanks = new Map();
  for (const side of [-1, 1]) {
    returning
      .filter((card) => Math.sign(card.getBoundingClientRect().left - targetLeft) === side)
      .sort((a, b) => side * (a.getBoundingClientRect().left - b.getBoundingClientRect().left))
      .forEach((card, rank) => returnRanks.set(card, { side, rank }));
  }

  // The card being landed on is brought back below, after the panel: when it
  // can appear depends on the path the panel takes to reach it.
  for (const card of returning) {
    const { side, rank } = returnRanks.get(card) ?? { side: 1, rank: 0 };
    gsap.fromTo(
      card,
      {
        opacity: 0,
        y: CARD_EXIT_DROP * (1 + rank * 0.14),
        rotation: side * CARD_EXIT_TILT * (1 + rank * 0.22),
        // same pivot the exit used, so the arc is the one being retraced
        transformOrigin: "50% 0%",
      },
      {
        opacity: 1,
        y: 0,
        rotation: 0,
        duration: CARD_EXIT_SECONDS,
        // back.out is back.in read backwards: the exit's wind-up becomes a
        // settle, so the pair are the same motion in opposite directions
        ease: CARD_RETURN_EASE,
        delay: FLIGHT_SECONDS * CARD_RETURN_DELAY + rank * CARD_EXIT_STAGGER,
        clearProps: "transform,transformOrigin",
      }
    );
  }

  // how much of the flight the destination card spends uncovered by the panel
  // heading for it; nothing to wait for if there is no panel in the air
  let landingCover = 0;

  if (detailPanel && panelFrom) {
    const panelTo = target.panel.getBoundingClientRect();
    const panelToCorners = readCorners(target.panel);

    // One shadow at a time.
    //
    // The card being landed on carries the same `0 18px 30px -10px` as the
    // panel shrinking into it, and by the end the two are cast from the same
    // box -- 0.2 alpha over 0.2 composites to 0.36, a band up to 27% darker
    // than either, heaviest below and to the sides where there is white page
    // to show it against. It blooms as the card comes up and halves again the
    // instant the panel is taken away, which read as the destination
    // announcing itself just before the landing. The pale sliver above was the
    // first half of that flash; this was the rest of it, and it was on every
    // character rather than only the one.
    //
    // Measured rather than reasoned about: holding the clock at t=0.98 and
    // taking the frame with and without the card changes 72,605 pixels, and
    // all of them but a 1px ring of edge antialiasing fall OUTSIDE the panel's
    // box -- 41k below, 13k left, 15k right, none above, which is that shadow
    // and nothing else.
    //
    // Going out there is no such moment: the clicked card is torn down with
    // the grid in the same frame the detail panel takes over its box, so one
    // shadow replaces the other. Coming back, the two overlap for the last
    // fifth of the flight. So the flying panel's shadow stands in for the
    // card's, and the card gets its own back on the frame the flying one
    // leaves.
    target.panel.style.boxShadow = "none";
    // out early, while the box is still big enough for the layout they were
    // written for; visibility follows so they cannot spill as it shrinks
    gsap.to(detailContents, {
      opacity: 0,
      duration: FLIGHT_SECONDS * DETAIL_TEXT_FADE,
      ease: "power2.in",
      onComplete: () => gsap.set(detailContents, { visibility: "hidden" }),
    });

    // The mirror of the expansion's corner handling.
    //
    // Lerping each corner from its own resting value to the card's meant the
    // two that rest square STARTED square -- the bottom-right of a panel that
    // is visibly a rounded sheet appeared as a hard point, then rounded up on
    // screen as it shrank. The expansion had the same fault in reverse and was
    // fixed the same way: one radius on all four corners, and the edges that
    // rest against the screen begin a full radius beyond it, so those corners
    // are out of sight at the moment they would have given the game away.
    const roles = cornerRoles(panelFromCorners);
    const [growingCorner] = roles.growing;
    const fromRadius = parseFloat(panelFromCorners[growingCorner]) || 0;
    const toRadius = parseFloat(panelToCorners[growingCorner]) || 0;

    // which edges rest on the viewport and therefore have somewhere to hide
    const sweepOnLeft = growingCorner === "borderTopLeftRadius";
    const farEdgeFlush = sweepOnLeft
      ? Math.abs(panelFrom.right - window.innerWidth) <= EDGE_FLUSH_SLOP
      : Math.abs(panelFrom.left) <= EDGE_FLUSH_SLOP;
    const bottomFlush = Math.abs(panelFrom.bottom - window.innerHeight) <= EDGE_FLUSH_SLOP;
    const run = fromRadius + PANEL_SIDE_OVERSHOOT;

    // where the panel actually starts: its resting box, pushed out on whichever
    // edges can take it, so nothing about the first frame looks different
    const startRight = farEdgeFlush && !sweepOnLeft ? panelFrom.right : panelFrom.right + (farEdgeFlush ? run : 0);
    const startLeft = farEdgeFlush && !sweepOnLeft ? panelFrom.left - run : panelFrom.left;
    const startBottom = bottomFlush ? panelFrom.bottom + run : panelFrom.bottom;

    // that start box against the card's, which is where the panel is headed
    landingCover = coverTime(
      { left: startLeft, top: panelFrom.top, right: startRight, bottom: startBottom },
      panelTo,
      PANEL_MORPH_EASE,
      LANDING_COVER_SLOP
    );

    const panelStyle = getComputedStyle(detailPanel);
    const basePadBottom = parseFloat(panelStyle.paddingBottom) || 0;
    const padSide = sweepOnLeft ? "paddingRight" : "paddingLeft";
    const basePadSide = parseFloat(panelStyle[padSide]) || 0;

    const baseEase = gsap.parseEase(PANEL_MORPH_EASE);
    const shrink = { t: 0 };
    gsap.to(shrink, {
      t: 1,
      duration: FLIGHT_SECONDS,
      ease: "none",
      onUpdate() {
        const e = baseEase(shrink.t);
        const left = lerp(startLeft, panelTo.left, e);
        const right = lerp(startRight, panelTo.right, e);
        const top = lerp(panelFrom.top, panelTo.top, e);
        const bottom = lerp(startBottom, panelTo.bottom, e);
        // only what is still past the edge needs cancelling out of the content
        // box; once the panel is inside the viewport there is nothing to cancel
        const belowFold = Math.max(0, bottom - window.innerHeight);
        const pastSide = sweepOnLeft
          ? Math.max(0, right - window.innerWidth)
          : Math.max(0, -left);
        const radius = lerp(fromRadius, toRadius, e);
        Object.assign(detailPanel.style, {
          left: `${left}px`,
          top: `${top}px`,
          width: `${right - left}px`,
          height: `${bottom - top}px`,
          paddingBottom: `${basePadBottom + belowFold}px`,
          [padSide]: `${basePadSide + pastSide}px`,
          // as on the way out: keep the gradient's dark corner on the part of
          // the box anyone can see, rather than out past the screen edge
          backgroundSize: `${right - left - pastSide}px ${bottom - top - belowFold}px`,
          backgroundPosition: `${sweepOnLeft ? 0 : pastSide}px 0px`,
          ...Object.fromEntries(CORNERS.map((corner) => [corner, `${radius}px`])),
        });
      },
      onComplete: () => {
        detailPanel.remove();
        target.panel.style.boxShadow = "";
      },
    });
  }

  // The card the character is landing on, held until the panel is over it --
  // never before the row it belongs to, and always opaque before the panel is
  // taken off it.
  const revealAt = Math.min(
    Math.max(CARD_RETURN_DELAY, landingCover),
    LANDING_CARD_OPAQUE_BY - CARD_RETURN_MIN
  );
  gsap.fromTo(
    target.card,
    { opacity: 0 },
    {
      opacity: 1,
      duration: FLIGHT_SECONDS * Math.min(CARD_RETURN_SECONDS, LANDING_CARD_OPAQUE_BY - revealAt),
      ease: "power2.out",
      delay: FLIGHT_SECONDS * revealAt,
    }
  );

  const stageRect = unionRect(from, to);
  mascotCanvas.style.left = `${stageRect.left}px`;
  mascotCanvas.style.top = `${stageRect.top}px`;
  mascotCanvas.style.width = `${stageRect.width}px`;
  mascotCanvas.style.height = `${stageRect.height}px`;

  const fromFraming = detailFraming(character);
  const toFraming = cardFraming(character);

  const apply = (t) => {
    const sharp = t <= FLIGHT_SHARP_EDGE || t >= 1 - FLIGHT_SHARP_EDGE;
    viewer.setSize(stageRect.width, stageRect.height, sharp ? 1 : FLIGHT_RENDER_SCALE);
    viewer.setProjectedFraming(
      lerpRect(from, to, t),
      stageRect,
      lerp(fromFraming.distanceScale, toFraming.distanceScale, t),
      lerp(fromFraming.verticalAnchor, toFraming.verticalAnchor, t)
    );
    viewer.render();
    dimAura?.(t);
  };
  const dimAura = flightAuraDimmer(viewer.canvas);
  apply(0);

  const progress = { t: 0 };
  gsap.to(progress, {
    t: 1,
    duration: FLIGHT_SECONDS,
    ease: "power3.inOut",
    onUpdate: () => apply(progress.t),
    onComplete() {
      // same rule as the forward landing: hand over only once the card's own
      // viewer has pixels in it, never merely because it exists
      let landed = false;
      const land = () => {
        if (landed) return;
        landed = true;
        clearTimeout(timer);
        target.canvas.style.visibility = "";
        mascotCanvas.remove();
        renderLoop.remove(viewer);
      };
      const timer = setTimeout(land, LANDING_TIMEOUT);
      if (cardReady) land();
      else whenCardReady = land;
    },
  });

  return true;
}

/* ------------------------------------------------------------------ */
/* Switching between expanded characters                              */
/* ------------------------------------------------------------------ */

const SWITCH_SECONDS = 0.55;
// the copy's cross-fade: 240ms, scale 0.95, origin left
const SWITCH_TEXT_SECONDS = 0.24;
const SWITCH_TEXT_SCALE = 0.95;
// how far the clip row drops as it leaves, and arrives from
const SWITCH_CLIPS_DROP = 44;
// how far each model travels, as a share of its own height. The outgoing one
// goes UP and the incoming arrives from below, so a swipe up carries you
// forward the way it does in any vertical feed.
const SWITCH_MODEL_TRAVEL = 0.5;

// Snaps an in-flight switch to its finished state. Set while one is running so
// the next one can land cleanly on top of it.
let endActiveSwitch = null;

// Moves between two expanded characters without moving the expanded slide
// itself. The panel stays exactly where it is and only its contents change:
// its colour washes over to the next character's, the copy cross-fades, the
// clip row drops away and the model is swapped top to bottom, following the
// chevron. Nothing relayouts and the page never slides.
function switchDetail(characterId, direction = 1) {
  // a second click while the first swap is still running would otherwise leave
  // two outgoing copies of everything behind
  endActiveSwitch?.();

  const view = app.firstElementChild;
  if (!view?.classList.contains("detail-view")) return false;
  const body = view.querySelector(".detail-body");
  const panel = view.querySelector(".detail-panel");
  const oldCanvas = view.querySelector(".detail-mascot-canvas");
  const oldInfo = view.querySelector(".detail-info");
  const oldClips = view.querySelector(".detail-clips");
  const rail = view.querySelector(".detail-rail");
  if (!body || !panel || !oldCanvas || !oldInfo || !oldClips || !rail) return false;

  const character = getCharacter(characterId);
  const index = characters.findIndex((c) => c.id === characterId);
  const nextCharacter = characters[(index + 1) % characters.length];

  // Freeze the outgoing character. Disposing its viewer stops it rendering but
  // leaves its canvas holding the last frame, so only the arriving model
  // actually draws while both are on screen.
  clearViewers();

  // --- the panel's colour ------------------------------------------------
  // A gradient cannot be tweened, so the next character's is laid over the
  // panel and faded up, then adopted as the panel's own. border-radius is
  // inherited so it follows the panel's corner exactly.
  const wash = document.createElement("div");
  wash.className = "panel-wash";
  wash.style.background = gradientCss(character);
  panel.insertBefore(wash, panel.firstChild);

  // --- the copy and the clip row ----------------------------------------
  // The outgoing pair are pinned to the boxes they already occupy and taken
  // out of flow, so the incoming pair lay out in exactly the slots they just
  // vacated. Switching the panel to a grid to overlap them instead would
  // change its layout algorithm mid-transition and shift the contents; this
  // leaves the panel a flex column throughout, which is what it is built as.
  const panelRect = panel.getBoundingClientRect();
  // Every box is measured BEFORE any of them is moved. Pinning one at a time
  // does not work: the panel is a flex column with justify-content
  // space-between, so the instant the copy goes position:absolute the clip row
  // is the only child left in flow and jumps to the top -- and that is then
  // the position it gets pinned at, so it flew up before dropping away.
  const pinned = [oldInfo, oldClips].map((el) => ({ el, r: el.getBoundingClientRect() }));
  for (const { el, r } of pinned) {
    Object.assign(el.style, {
      position: "absolute",
      left: `${r.left - panelRect.left}px`,
      top: `${r.top - panelRect.top}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
      margin: "0",
      pointerEvents: "none",
    });
    el.classList.add("switch-pane");
  }

  const newInfo = buildDetailInfo(character);
  const newClips = buildClips(character);
  newInfo.classList.add("switch-pane");
  newClips.classList.add("switch-pane");
  panel.appendChild(newInfo);
  panel.appendChild(newClips);

  // --- the model ---------------------------------------------------------
  const newCanvas = buildMascotCanvas(character);
  body.appendChild(newCanvas);
  mountMascot(newCanvas, character, null);

  // +1 runs the swap UPWARD: the outgoing character leaves out of the top and
  // the next arrives from below, the way a vertical feed moves, so swiping the
  // finger up means "next". -1 mirrors the whole thing for going back.
  const dir = direction < 0 ? -1 : 1;
  const travel = oldCanvas.getBoundingClientRect().height * SWITCH_MODEL_TRAVEL * dir;

  const tl = gsap.timeline();
  tl.to(wash, { opacity: 1, duration: SWITCH_SECONDS * 0.7, ease: "power2.inOut" }, 0);

  // copy and clips cross-fade
  tl.to(oldInfo, { opacity: 0, scale: SWITCH_TEXT_SCALE, duration: SWITCH_TEXT_SECONDS, ease: "power2.in" }, 0);
  tl.fromTo(
    newInfo,
    { opacity: 0, scale: SWITCH_TEXT_SCALE },
    { opacity: 1, scale: 1, duration: SWITCH_TEXT_SECONDS, ease: "power2.out" },
    SWITCH_TEXT_SECONDS * 0.8
  );

  // Exactly the copy's exit, plus the travel. The two already shared a start,
  // a duration and an ease, so they were leaving together -- but the copy
  // scaled away while the clips only slid, and two different treatments read as
  // two separate departures. The scale is what makes them one. They share
  // transform-origin: left center (.switch-pane), so it pulls the same way.
  tl.to(
    oldClips,
    {
      opacity: 0,
      scale: SWITCH_TEXT_SCALE,
      y: -SWITCH_CLIPS_DROP * dir,
      duration: SWITCH_TEXT_SECONDS,
      ease: "power2.in",
    },
    0
  );
  // in from BELOW, not down from above: the whole swap runs one way up the
  // screen, so the clip row arriving has to travel with the model rather than
  // against it
  tl.fromTo(
    newClips,
    { opacity: 0, y: SWITCH_CLIPS_DROP * dir },
    { opacity: 1, y: 0, duration: SWITCH_TEXT_SECONDS * 1.3, ease: "power2.out" },
    SWITCH_TEXT_SECONDS * 0.8
  );

  // this one out of the top, the next in from the bottom
  // The one leaving goes quicker than the one arriving, and on a harder curve.
  // Matching their speeds left two characters sharing the frame for most of
  // the switch, which is what made it feel crowded rather than smooth -- the
  // outgoing one has nothing left to say, so it should clear out and leave the
  // arrival to be the thing you watch.
  // Opacity is tweened SEPARATELY from the travel, and much faster. Riding the
  // same tween meant the arriving character eased in over the whole distance --
  // measured at 0.49 opaque 180ms in and still 0.86 at 270ms, by which point it
  // is sitting over the panel. A solid 3D model you can see the background
  // through reads as a glitch, not as a transition. Now it is opaque within
  // ~90ms, while it is still low and moving, and the travel keeps its own
  // longer curve. The outgoing one gets the same treatment on the way out, so
  // the two are never both half-there at once.
  tl.to(oldCanvas, { y: -travel, duration: SWITCH_SECONDS * 0.38, ease: "power3.in" }, 0);
  tl.to(oldCanvas, { opacity: 0, duration: SWITCH_SECONDS * 0.22, ease: "power2.in" }, SWITCH_SECONDS * 0.14);
  tl.fromTo(
    newCanvas,
    { y: travel, opacity: 0 },
    { y: 0, duration: SWITCH_SECONDS * 0.7, ease: "power3.out" },
    SWITCH_SECONDS * 0.22
  );
  tl.to(newCanvas, { opacity: 1, duration: SWITCH_SECONDS * 0.16, ease: "power2.out" }, SWITCH_SECONDS * 0.22);

  // --- the rail ----------------------------------------------------------
  rail.innerHTML = `
    <span class="rail-tab is-active">${character.name}</span>
    <div class="rail-next-group">
      <button class="rail-tab" type="button">${nextCharacter.name}</button>
      <button class="rail-chevron" type="button" aria-label="Next character">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
  const goNext = () => showDetail(nextCharacter.id);
  rail.querySelector(".rail-tab:not(.is-active)").addEventListener("click", goNext);
  rail.querySelector(".rail-chevron").addEventListener("click", goNext);

  // The NAMES fade, and only the names.
  //
  // This used to fade the whole rail with a fromTo, which flashed twice over.
  // Once because the chevron was dragged through a transition it has no part
  // in -- it is the same arrow before and after, so animating it is pure
  // blinking. And once because a fromTo does not apply its "from" until the
  // timeline's first tick: the new markup above had already been painted at
  // full opacity by then, so it appeared, snapped to nothing, and faded back
  // in.
  //
  // Hiding them here, in the same synchronous task that wrote the markup,
  // means there is no frame between the two for the browser to paint.
  const tabs = rail.querySelectorAll(".rail-tab");
  gsap.set(tabs, { opacity: 0 });
  tl.to(tabs, { opacity: 1, duration: SWITCH_TEXT_SECONDS, ease: "power2.out" }, 0);

  const finish = () => {
    if (endActiveSwitch !== finish) return;
    endActiveSwitch = null;
    tl.kill();
    // the wash becomes the panel's own background, and every outgoing piece goes
    paintPanel(panel, character);
    wash.remove();
    oldInfo.remove();
    oldClips.remove();
    oldCanvas.remove();
    for (const el of [newInfo, newClips, newCanvas]) {
      el.classList.remove("switch-pane");
      gsap.set(el, { clearProps: "opacity,transform" });
    }
    // only now: until oldCanvas is off the screen and out of the DOM, the
    // character it was drawing is still being looked at
    releaseAwayFromDetail(characterId);
  };
  endActiveSwitch = finish;
  tl.eventCallback("onComplete", finish);

  return true;
}

// The expanded view's half of the sliding window.
//
// The grid has one: mountNearby
// keeps the cards within a card's reach of the screen and releaseDistant hands
// back everything past it, so paging the carousel on a phone holds three
// characters and no more.
//
// Switching characters INSIDE the expanded view had no equivalent, and the
// omission is invisible from the code that does it: clearViewers disposes each
// viewer, which gives up its claim on the model, but giving up a claim is not
// the same as freeing it. releaseModel is the only thing that drops the parsed
// scene and its geometry, and nothing on this path called it -- so every
// character visited here stayed decoded. Measured: switching through all six
// left all six resident, which is the condition that used to kill the tab.
//
// Keeps the same three the grid keeps: the character on screen and the one
// either side of it, which are what a swipe in either direction reaches and
// what the rail names. releaseModel declines to free anything still claimed by
// a live viewer, so this cannot pull a character out from under the view.
function releaseAwayFromDetail(characterId) {
  if (!MEMORY_CONSTRAINED) return;
  const index = characters.findIndex((c) => c.id === characterId);
  if (index < 0) return;
  const count = characters.length;
  const keep = new Set([
    characterId,
    characters[(index + 1) % count].id,
    characters[(index - 1 + count) % count].id,
  ]);
  for (const character of characters) {
    if (!keep.has(character.id)) releaseModel(character.model);
  }
}

// A playing embed is a live document with sound, and the flight back to the
// grid takes long enough for it to be heard over a view it no longer belongs
// to. Switching characters tears its clip row out of the DOM, which stops it;
// closing does not, so it is stopped here.
function stopAllClips() {
  for (const card of document.querySelectorAll(".clip-card.is-playing")) {
    card.dispatchEvent(new CustomEvent("clip:stop"));
  }
}

function showGrid() {
  stopAllClips();
  resetYawFollow();
  state.view = "grid";
  // flies the character back into its card when there is one in the air to
  // fly; falls back to a plain mount otherwise (first load, or a failed flight)
  if (flyToGrid(state.detailId)) return null;
  const view = mount(buildGridView);
  // come back to the page the character was on, rather than always to page one
  view.revealCard?.(state.detailId);
  // handed back so the first load can deal its cards in; every other caller
  // ignores it
  return view;
}

const FLIGHT_SECONDS = 0.62;
// render fewer pixels while the character is IN MOTION and let the browser
// scale the bitmap up; at speed the softness is invisible and it keeps the
// per-frame cost well clear of the frame budget.
//
// Applied only through the middle of the flight. Holding it across the whole
// move meant the first painted frame was 25% softer than the card the user had
// just been looking at, and the last one 25% softer than the mascot that
// replaced it -- a sharpness pop at each end of an otherwise continuous move,
// with nothing moving to hide it.
const FLIGHT_RENDER_SCALE = 0.75;
// How much of each end of the flight renders at full resolution. power3.inOut
// is at its slowest here, so these are both the frames where softness reads
// most plainly and the frames that sit against a static card or mascot. It
// cannot be the endpoints alone: the t=1 frame is never painted -- the landing
// swap replaces it within the same frame -- so the match has to be in place
// several frames before the end.
const FLIGHT_SHARP_EDGE = 0.12;

// What the flight's halo shrinks to at the midpoint, as a share of the resting
// radius. Phones only.
//
// Exactly the bargain FLIGHT_RENDER_SCALE above already strikes, applied to the
// other per-frame cost: through the fast middle the character is moving too
// quickly for the difference to register, and the fast middle is where the
// frames are most expensive. Both ends sit at the full radius, so the hand-off
// to the resting aura is an identity rather than a step.
//
// It is worth this much because of where the cost comes from. The flight
// canvas is the union of both rects -- around 3M device pixels at a phone's
// DPR, most of them empty -- and a drop-shadow re-blurs that entire region on
// every frame. Radius is the only term in that we can actually move.
const FLIGHT_AURA_MIN = 0.34;

// Eases the halo down through the middle of a flight and back up by the end.
// Null on anything but a phone, where the full radius is affordable throughout.
function flightAuraDimmer(canvas) {
  if (!isPhone()) return null;
  const full = parseFloat(getComputedStyle(canvas).getPropertyValue("--aura-size"));
  if (!full) return null;
  // sin is 0 at both ends and 1 at the midpoint, with no corner at either end
  // -- a linear ramp would start shrinking the halo the instant the character
  // moved, which is the frame you are most likely to be looking at it in
  return (t) => {
    const dip = Math.sin(Math.PI * Math.min(1, Math.max(0, t)));
    const radius = full * (1 - (1 - FLIGHT_AURA_MIN) * dip);
    canvas.style.setProperty("--aura-size", `${radius.toFixed(1)}px`);
  };
}
// if the mascot's model somehow never resolves, don't strand the flight canvas
// on top of the page forever
const LANDING_TIMEOUT = 2000;

// The cards that were NOT clicked don't disappear with the grid -- they drop
// away and fade while the chosen one flies. Well short of the flight so the
// grid has cleared long before the detail panel finishes growing over it.
const CARD_EXIT_SECONDS = 0.3;
// how far a card falls, in px. Scaled up slightly with distance from the
// clicked card so the row spreads apart as it goes rather than moving as a
// single slab.
const CARD_EXIT_DROP = 84;
// degrees. Cards to the left of the clicked one tilt one way and cards to the
// right the other, so the row parts around the character being chosen.
const CARD_EXIT_TILT = 4.5;
// each card further out starts this much later, rippling outward from the pick
const CARD_EXIT_STAGGER = 0.04;

// The clicked card's own title and movie line are carried across the flight as
// a ghost, because nothing else covers that gap: the real card dies with the
// grid, and the detail panel's contents cannot appear until late (they are
// laid out against a padding-left of up to 620px, so at card width the badge
// spills out and the description clips). Without this the text blinks out on
// the very first frame of the flight and fades back in two thirds of the way
// through -- read as a flash right at the click.
//
// It must be GONE before the panel's own text arrives, not crossfaded with it
// -- two pieces of text stacked mid-flight is what reads as unsettling. Hence
// the early window and the out curve: an in curve holds near full opacity and
// dumps at the end, which is exactly the overlap being avoided.
const CARD_TEXT_FADE_FROM = 0.2;
const CARD_TEXT_FADE_TO = 0.46;
// the grid footer is chrome rather than a card, so it just fades rather than
// dropping and tilting -- but it has to go somewhere, not vanish with the grid
const FOOTER_FADE_SECONDS = 0.22;
// back.in loads up against the drop before releasing it -- the card lifts and
// counter-rotates a few pixels first, which is what reads as spring rather
// than as a card simply being translated downward.
const CARD_EXIT_EASE = "back.in(2.2)";

// How much the panel's bottom edge runs ahead of its top edge while the box
// grows. 0 drives both off one ease, which is what made the expansion read as
// going up-and-away from the card: the end state only puts a little more
// travel below the card than above it (167px down vs 145px up at 1440x900),
// and the box is racing ~685px leftward at the same time, so an even vertical
// split gets read as upward. Running the bottom down early makes the panel
// descend as it opens. Both edges still start and finish together.
const PANEL_DOWNWARD_BIAS = 0.45;

// How far past the bottom of the viewport the panel's bottom edge travels,
// over and above its final radius. The panel keeps growing downward until its
// rounded bottom-left corner is entirely below the fold -- like dragging a
// sheet of paper down behind the screen edge -- so the corner never has to
// straighten on screen. Once it is off the fold the stylesheet's own square
// bottom is indistinguishable from it, and the landing hand-off is invisible.
const PANEL_BOTTOM_OVERSHOOT = 8;

// The same trick applied to the panel's far vertical edge.
//
// The complaint it answers: on a phone the panel lands flush against the
// viewport's left edge, so its right edge travels 16px while its left travels
// 47px. The right is effectively already there, which leaves the left as the
// only edge you actually watch -- and on power3.inOut it spends the first
// third of the flight covering 6% of its distance. Then, at the very end, its
// two corners crush from the card's 40px to square in the last 0.15 of the
// move, on screen, while the edge sits at x=0.
//
// So the left edge does what the bottom edge already does: it keeps going
// until its corners are entirely off the side, and never straightens at all.
// The stylesheet's resting box -- square, flush at 0 -- is indistinguishable
// from it, which is what makes the landing hand-off invisible.
//
// Applies to both layouts. The phone's far edge is the left one (its panel is
// left:0), the desktop's is the right one (right:0); both finish flush against
// the screen, so both can run past it. With this, no corner anywhere in the
// expansion straightens while it is visible.
const PANEL_SIDE_OVERSHOOT = 8;

// How much the far edge leads the near one, in the same shape as
// PANEL_DOWNWARD_BIAS: one base ease, warped by an exponent, both ends still
// pinned. It exists for the same reason -- an edge with three times the
// distance to cover needs to leave earlier, or the box reads as growing from
// one side only.
const PANEL_SIDE_BIAS = 0.35;

// The far edge only earns the overshoot where it finishes flush against the
// viewport; anywhere else the move would be visible as the panel running
// somewhere it does not belong.
const EDGE_FLUSH_SLOP = 1.5;

// Carries the character from its card into the detail layout as one continuous
// move rather than cross-fading between two separately drawn copies.
//
// The card's own viewer is adopted: its canvas is lifted out of the card and
// pinned to the viewport, so the same scene, camera and GPU textures are on
// screen the whole way. Nothing is reloaded -- only possible now that every
// viewer shares one context.
//
// The canvas is sized ONCE, to the union of the start and end rects, and never
// touched again. Assigning canvas.width/height reallocates the entire backing
// store (megabytes per frame at retina detail size) and wipes it; doing that
// per frame is what made the first attempt flash and teleport. Here the
// character is moved and grown purely by camera framing.
function flyToDetail(character, cardCanvas) {
  const viewer = state.viewers.find((v) => v.canvas === cardCanvas);
  if (!viewer) return false;

  const from = cardCanvas.getBoundingClientRect();
  if (!from.width || !from.height) return false;

  dropOutgoingCards();

  // everything below needs the clicked card's own DOM, so resolve it before
  // the canvas is lifted out of it
  const clickedCard = cardCanvas.closest(".char-card");

  // the card's coloured panel, measured before the grid is torn down -- the
  // detail panel will start life at exactly this box so it reads as this card
  // growing rather than a new panel appearing
  const cardPanel = clickedCard?.querySelector(".char-card-panel");
  const panelFrom = cardPanel?.getBoundingClientRect();
  const panelFromCorners = cardPanel ? readCorners(cardPanel) : null;

  // The cards that are staying behind, measured while the grid is still laid
  // out. Only the ones actually on screen: the carousel can be scrolled, and
  // there is no point animating cards parked off the side of the viewport.
  const leaving = [...app.querySelectorAll(".char-card")]
    .filter((card) => card !== clickedCard)
    .map((card) => ({ card, rect: card.getBoundingClientRect() }))
    .filter(({ rect }) => rect.right > 0 && rect.left < window.innerWidth);

  const clickedLeft = clickedCard?.getBoundingClientRect().left ?? 0;

  // measured before the teardown, pinned after it
  const gridFooter = app.querySelector(".grid-footer");
  const footerFrom = gridFooter?.getBoundingClientRect();

  // hand the viewer to the flight before clearViewers() can dispose it
  state.viewers = state.viewers.filter((v) => v !== viewer);

  // Dispose the other cards' viewers FIRST, before the grid is repositioned
  // below. dispose() only stops them rendering and disconnects their
  // ResizeObservers -- it leaves each 2D canvas holding its last painted
  // frame, which is exactly what the exit animation needs. Order matters:
  // setSize() does not check `disposed`, so an observer still live when the
  // grid moves would assign canvas.width and wipe the very pixels being
  // faded out.
  clearViewers();

  // The cards fly out rather than vanishing. Each is pinned to the viewport at
  // the exact box it already occupies and lifted onto <body>, which buys three
  // things at once: the teardown below cannot delete them, position:fixed
  // escapes .carousel-track's overflow (hidden on one axis clips BOTH, so a
  // card sliding down inside the track would be sheared off at its edge), and
  // nothing has to re-resolve the grid's flex layout -- pinning the whole
  // .grid-view instead made its justify-content:flex-end column rebuild on
  // re-parent and threw every card to the bottom of the viewport for a frame.
  //
  // z-index 2 puts them above the detail view's background but below the rail,
  // close row and growing panel, so the panel sweeps over them as it expands.
  // Written straight to the element rather than through gsap.set: this is
  // layout-critical setup, not animation, and it has to be in effect in this
  // same task -- the card is about to be re-parented onto <body>, where
  // without it a .char-card is just an inline-block button in normal flow.
  // gsap.set() only renders through the global timeline, so anything that
  // pauses that timeline leaves the pin unapplied.
  for (const { card, rect } of leaving) {
    Object.assign(card.style, {
      position: "fixed",
      margin: "0",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      zIndex: "2",
      pointerEvents: "none",
    });
    document.body.appendChild(card);
    outgoingCards.push(card);
  }

  // A copy of the clicked card's panel, stripped to just its text. Cloning the
  // whole panel rather than lifting the title and movie line individually is
  // what makes the text land exactly where it already was -- it inherits the
  // panel's own padding, font sizes and flex-end alignment for free, so there
  // is no second set of numbers to keep in step with the stylesheet. The
  // background and shadow come off because the real detail panel is already
  // drawing those, directly underneath.
  let cardTextGhost = null;
  if (cardPanel && panelFrom) {
    cardTextGhost = cardPanel.cloneNode(true);
    Object.assign(cardTextGhost.style, {
      position: "fixed",
      margin: "0",
      left: `${panelFrom.left}px`,
      top: `${panelFrom.top}px`,
      width: `${panelFrom.width}px`,
      height: `${panelFrom.height}px`,
      background: "none",
      boxShadow: "none",
      // above the growing panel (5), below the character in flight (60)
      zIndex: "6",
      pointerEvents: "none",
    });
    document.body.appendChild(cardTextGhost);
    outgoingCards.push(cardTextGhost);
  }

  if (gridFooter && footerFrom) {
    Object.assign(gridFooter.style, {
      position: "fixed",
      margin: "0",
      left: `${footerFrom.left}px`,
      top: `${footerFrom.top}px`,
      width: `${footerFrom.width}px`,
      height: `${footerFrom.height}px`,
      zIndex: "2",
      pointerEvents: "none",
    });
    document.body.appendChild(gridFooter);
    outgoingCards.push(gridFooter);
  }

  // the clicked card goes with the grid: its character is already the flight
  // canvas, and its panel is handed to the detail panel, which starts at
  // exactly that box with the same gradient
  app.innerHTML = "";
  // Build the destination at its final size, and WITHOUT the usual fadeIn():
  // that enters from scale(0.96), so any rect measured under it is 4% short
  // and the flight lands in the wrong place. The mascot's own viewer is
  // deferred until landing so it isn't rendering full-size alongside us.
  const view = buildDetailView(character.id, { deferMascot: true });
  app.appendChild(view);
  const unpinText = pinTextWidth(view);

  const target = view.querySelector(".detail-mascot-canvas");
  const to = target.getBoundingClientRect();
  target.style.visibility = "hidden";

  // Grow the REAL detail panel from the card's box rather than cross-fading a
  // copy: both already carry the same character gradient, so starting it at
  // the card's rect and corners makes it the same object visually. Animating
  // the box itself (not a transform: scale) is what keeps the corner radii
  // circular and the gradient unstretched while it grows.
  const panel = view.querySelector(".detail-panel");
  if (panel && panelFrom) {
    const panelTo = panel.getBoundingClientRect();
    const panelToCorners = readCorners(panel);
    // Contents stay hidden while the box resizes. visibility, not just opacity:
    // they are laid out against the panel's *current* (smaller) width, so at
    // any opacity above zero the badge spills past the panel's right edge and
    // the description is clipped mid-sentence. They appear only once the box
    // is close enough to final size for the layout to be the real one.
    const contents = [...panel.children];
    gsap.set(contents, { opacity: 0, visibility: "hidden" });
    // While growing, the panel sweeps across the rail's column (which it never
    // touches once settled, sitting at --detail-inset). The rail is z-index 3,
    // so without this its vertical name labels show straight through the
    // growing box. Cleared on landing so normal stacking resumes.
    // Written straight to the element, like the card pins above: the box this
    // establishes is what every frame below is measured against, so it has to
    // hold in this same task rather than waiting on the global timeline.
    Object.assign(panel.style, {
      position: "fixed",
      zIndex: "5",
      left: `${panelFrom.left}px`,
      top: `${panelFrom.top}px`,
      width: `${panelFrom.width}px`,
      height: `${panelFrom.height}px`,
      ...panelFromCorners,
    });
    // The corners are split by what they are actually doing.
    //
    // Top-left is the detail panel's signature sweep and the one the eye
    // follows, on screen the whole way. Growing it in step with the box, on
    // the box's own ease, makes the radius read as part of the same expansion
    // rather than something that happens to the panel after it arrives.
    const roles = cornerRoles(panelToCorners);
    const pick = (corners) =>
      Object.fromEntries(corners.map((corner) => [corner, panelToCorners[corner]]));

    // Which way the far edge would have to run to leave the screen, and whether
    // that is possible: only if the edge actually lands flush against the
    // viewport, since anywhere else the overshoot would be visible as the
    // panel running somewhere it does not belong.
    //
    // Both layouts qualify, which is why there is no breakpoint here. The
    // phone's panel is left:0 and its far edge is the left one; the desktop's
    // is right:0 and its far edge is the right one. Either way it ends on the
    // screen edge and can keep going past it.
    const lateOnLeft = roles.growing[0] === "borderTopRightRadius";
    const lateEdgeFlush = lateOnLeft
      ? Math.abs(panelTo.left) <= EDGE_FLUSH_SLOP
      : Math.abs(panelTo.right - window.innerWidth) <= EDGE_FLUSH_SLOP;
    const sideOvershoot = lateEdgeFlush;

    // Only when the far edge cannot leave the screen. Where it can, every
    // corner is driven together in growPanel below.
    if (!sideOvershoot) {
      gsap.to(panel, {
        ...pick(roles.growing),
        duration: FLIGHT_SECONDS,
        ease: "power3.inOut",
      });
    }

    // The far pair are headed for a square 0, and a corner going square while
    // the box is still moving reads as the panel straightening out before it
    // has landed. They hold the card's own radius and only square off once the
    // box is essentially in position.
    //
    // Skipped entirely when the far edge is leaving the screen. There the
    // panel keeps ONE radius on all four corners the whole way -- see
    // growPanel -- so there is no second behaviour to time.
    if (!sideOvershoot) {
      gsap.to(panel, {
        ...pick(roles.late),
        duration: FLIGHT_SECONDS * (1 - CORNER_SPLIT),
        ease: "power2.inOut",
        delay: FLIGHT_SECONDS * CORNER_SPLIT,
      });
    }

    // Bottom-left starts on the card's radius exactly as top-left does, so it
    // can simply BE top-left for the whole move: the two rise together and the
    // entire left edge rounds as the panel opens, with no second phase and no
    // switch of behaviour to hide.
    const [growingCorner] = roles.growing;
    const fromGrowing = parseFloat(panelFromCorners[growingCorner]) || 0;
    const toGrowing = parseFloat(panelToCorners[growingCorner]) || 0;
    const joiningCorner = (base) => lerp(fromGrowing, toGrowing, base);
    // every corner, or just the one that stays on screen plus its neighbour,
    // depending on whether the others have anywhere to hide
    const cornerRadii = (radius) =>
      Object.fromEntries(
        (sideOvershoot ? CORNERS : [roles.joining]).map((corner) => [corner, `${radius}px`])
      );

    // ...which only works because the bottom edge outruns the viewport. The
    // corner's curve occupies the last `radius` pixels of the left edge, so
    // once the bottom sits a full radius below the fold the curve is entirely
    // out of sight and the edge reads as straight all the way down. The
    // stylesheet's resting box -- square, ending exactly at the fold -- looks
    // identical, which is what makes the hand-off on landing invisible.
    const bottomTo = panelTo.bottom + toGrowing + PANEL_BOTTOM_OVERSHOOT;

    // Growing past the fold would otherwise drag the panel's contents down
    // with it, since they are laid out with space-between against whatever
    // height the box currently has. Adding the overshoot to the bottom padding
    // cancels it out of the content box exactly (box-sizing is border-box
    // throughout), so the text, badge and clips sit where they always did.
    const panelStyle = getComputedStyle(panel);
    const basePadBottom = parseFloat(panelStyle.paddingBottom) || 0;

    // How far past the side the far edge has to run before its corners are out
    // of sight. The curve occupies the last `radius` pixels of that edge, and
    // every corner now grows to the panel's own big radius rather than holding
    // the card's small one -- so it is that final radius that has to clear the
    // screen, exactly as the bottom clears the fold by toGrowing above.
    const sideRun = sideOvershoot ? toGrowing + PANEL_SIDE_OVERSHOOT : 0;
    const lateTo = lateOnLeft ? panelTo.left - sideRun : panelTo.right + sideRun;
    const basePadSide = parseFloat(
      lateOnLeft ? panelStyle.paddingLeft : panelStyle.paddingRight
    ) || 0;
    const padSide = lateOnLeft ? "paddingLeft" : "paddingRight";

    // The box is driven edge by edge rather than as one rect, so the bottom
    // can run ahead of the top (see PANEL_DOWNWARD_BIAS). Both warps are of
    // the SAME base ease and both still map 0 to 0 and 1 to 1, so the panel
    // starts on the card and lands on the stylesheet's layout exactly -- only
    // the path between them changes.
    const baseEase = gsap.parseEase(PANEL_MORPH_EASE);
    const growPanel = (t) => {
      const base = baseEase(t);
      const down = base ** (1 - PANEL_DOWNWARD_BIAS);
      const up = base ** (1 + PANEL_DOWNWARD_BIAS);
      // the far edge leads the near one by the same warp, and only when it is
      // the one running off the side -- otherwise both stay on the base curve
      const far = sideOvershoot ? base ** (1 - PANEL_SIDE_BIAS) : base;
      const near = sideOvershoot ? base ** (1 + PANEL_SIDE_BIAS) : base;
      const top = lerp(panelFrom.top, panelTo.top, up);
      const bottom = lerp(panelFrom.bottom, bottomTo, down);
      const left = lerp(panelFrom.left, lateOnLeft ? lateTo : panelTo.left, lateOnLeft ? far : near);
      const right = lerp(panelFrom.right, lateOnLeft ? panelTo.right : lateTo, lateOnLeft ? near : far);
      // only what has actually gone past the edge needs cancelling; before
      // that the panel is still a card-sized box on screen
      const belowFold = Math.max(0, bottom - panelTo.bottom);
      const pastSide = lateOnLeft
        ? Math.max(0, panelTo.left - left)
        : Math.max(0, right - panelTo.right);
      Object.assign(panel.style, {
        left: `${left}px`,
        top: `${top}px`,
        width: `${right - left}px`,
        height: `${bottom - top}px`,
        paddingBottom: `${basePadBottom + belowFold}px`,
        [padSide]: `${basePadSide + pastSide}px`,
        // The panel's gradient runs 135deg, so its darkest stop sits at the
        // box's bottom-right -- and the overshoot puts that corner off the
        // screen, draining the tint mid-flight and snapping it back at the
        // landing. Sizing the background to what is actually visible pins the
        // dark end to the corner the eye can see, all the way through.
        backgroundSize: `${right - left - pastSide}px ${bottom - top - belowFold}px`,
        backgroundPosition: `${lateOnLeft ? pastSide : 0}px 0px`,
        // One radius on every corner, all the way. The panel in flight is a
        // single shape rather than one signature corner and three leftovers
        // from the card -- which is what it looked like when the far pair held
        // 40px while this one grew to 158.
        //
        // None of them squares off during the move. The other three simply
        // leave the screen still round: the bottom runs a full radius past the
        // fold, the far side a full radius past the screen edge. Square is only
        // the stylesheet's RESTING shape, where those edges sit on the viewport
        // edges and a curve would notch the corner of the window -- and it is
        // swapped in at the landing, by which point nothing can see it happen.
        ...cornerRadii(joiningCorner(base)),
      });
    };

    const grow = { t: 0 };
    gsap.to(grow, {
      t: 1,
      duration: FLIGHT_SECONDS,
      // the shaping lives in growPanel, so the tween itself runs on raw time
      ease: "none",
      onUpdate: () => growPanel(grow.t),
      onComplete() {
        // hand the panel back to the stylesheet's own layout
        gsap.set(panel, {
          clearProps:
            "position,zIndex,left,top,width,height,paddingBottom,paddingLeft," +
            "paddingRight,backgroundSize,backgroundPosition," +
            CORNERS.join(","),
        });
        gsap.set(contents, { clearProps: "visibility" });
        unpinText?.();
      },
    });
    gsap.to(contents, {
      opacity: 1,
      visibility: "visible",
      duration: 0.3,
      ease: "power2.out",
      // power3.inOut has the box ~95% grown by here, so the text lands in a
      // layout that is already the final one
      delay: FLIGHT_SECONDS * 0.72,
    });
  }

  const stageRect = unionRect(from, to);
  cardCanvas.classList.add("flight-canvas");
  cardCanvas.style.left = `${stageRect.left}px`;
  cardCanvas.style.top = `${stageRect.top}px`;
  cardCanvas.style.width = `${stageRect.width}px`;
  cardCanvas.style.height = `${stageRect.height}px`;
  document.body.appendChild(cardCanvas);

  const fromFraming = cardFraming(character);
  const toFraming = detailFraming(character);

  const apply = (t) => {
    // Full detail across both ends of the flight, reduced only through the
    // fast middle. setSize early-returns when the pixel size is unchanged, so
    // this reallocates exactly twice across the whole flight (entering and
    // leaving the fast stretch) -- and both
    // happen in the same task as the render below, so the wipe a reallocation
    // causes is never what gets painted.
    const sharp = t <= FLIGHT_SHARP_EDGE || t >= 1 - FLIGHT_SHARP_EDGE;
    const renderScale = sharp ? 1 : FLIGHT_RENDER_SCALE;
    viewer.setSize(stageRect.width, stageRect.height, renderScale);
    viewer.setProjectedFraming(
      lerpRect(from, to, t),
      stageRect,
      lerp(fromFraming.distanceScale, toFraming.distanceScale, t),
      lerp(fromFraming.verticalAnchor, toFraming.verticalAnchor, t)
    );
    viewer.render();
    dimAura?.(t);
  };
  const dimAura = flightAuraDimmer(viewer.canvas);
  apply(0);

  // Opacity only, never transform: the panel and character are both flying
  // toward rects measured against this layout, so it must not move. The panel
  // itself is excluded -- it grows opaque rather than fading in.
  const chrome = [view.querySelector(".close-row"), view.querySelector(".detail-rail")].filter(Boolean);
  gsap.fromTo(
    chrome,
    { opacity: 0 },
    {
      opacity: 1,
      duration: FLIGHT_SECONDS * CHROME_FADE,
      ease: "power2.out",
      delay: FLIGHT_SECONDS * CHROME_FADE_DELAY,
    }
  );

  // the card's text holds its place while the panel grows out from under it,
  // then hands over to the detail panel's own title as that fades in
  if (cardTextGhost) {
    gsap.to(cardTextGhost, {
      opacity: 0,
      duration: FLIGHT_SECONDS * (CARD_TEXT_FADE_TO - CARD_TEXT_FADE_FROM),
      // out, not in. power2.in spends its window near full opacity and dumps at
      // the end -- this leaves promptly and eases into gone.
      ease: "power2.out",
      delay: FLIGHT_SECONDS * CARD_TEXT_FADE_FROM,
      onComplete() {
        cardTextGhost.remove();
      },
    });
  }

  if (gridFooter) {
    gsap.to(gridFooter, {
      opacity: 0,
      duration: FOOTER_FADE_SECONDS,
      ease: "power2.in",
      onComplete() {
        gridFooter.remove();
      },
    });
  }

  // The cards left behind drop, tilt and fade instead of being cut. They are
  // static bitmaps by now (their viewers are disposed), so this is pure
  // compositor work and costs the flight nothing.
  if (leaving.length) {
    // ranked by how far each card sits from the clicked one, left and right
    // counted separately, so the stagger ripples outward from the pick rather
    // than sweeping across the row in DOM order
    const ranks = new Map();
    for (const side of [-1, 1]) {
      leaving
        .filter(({ rect }) => Math.sign(rect.left - clickedLeft) === side)
        .sort((a, b) => side * (a.rect.left - b.rect.left))
        .forEach(({ card }, rank) => ranks.set(card, { side, rank }));
    }

    // collects only the cards it animated -- the text ghost and the footer are
    // on their own, longer schedules and must not be swept up with them
    const exit = gsap.timeline({
      onComplete() {
        for (const { card } of leaving) card.remove();
      },
    });
    for (const { card } of leaving) {
      const { side, rank } = ranks.get(card) ?? { side: 1, rank: 0 };
      const at = rank * CARD_EXIT_STAGGER;
      exit.to(
        card,
        {
          y: CARD_EXIT_DROP * (1 + rank * 0.14),
          rotation: side * CARD_EXIT_TILT * (1 + rank * 0.22),
          duration: CARD_EXIT_SECONDS,
          ease: CARD_EXIT_EASE,
          // pivot at the top edge, so the tilt swings each card's weight out
          // and down rather than spinning it about its middle
          transformOrigin: "50% 0%",
        },
        at
      );
      // Fade kept off the springy ease and on its own curve: back.in starts by
      // running backwards, which on opacity means overshooting past fully
      // opaque and then dumping to zero at the very end. Holding the card
      // visible through the wind-up and fading it as it actually leaves is
      // what makes the spring legible.
      exit.to(
        card,
        { opacity: 0, duration: CARD_EXIT_SECONDS * 0.8, ease: "power2.in" },
        at + CARD_EXIT_SECONDS * 0.2
      );
    }
  }

  const progress = { t: 0 };
  gsap.to(progress, {
    t: 1,
    duration: FLIGHT_SECONDS,
    ease: "power3.inOut",
    onUpdate: () => apply(progress.t),
    onComplete() {
      // The handover has to wait for the mascot to have PIXELS, not merely to
      // exist. Revealing it and dropping the flight canvas in this task left a
      // frame with the character nowhere on screen at all (measured: zero
      // character pixels) -- the flight canvas gone, the mascot still blank
      // because the render loop's rAF had not come round yet. The mascot's
      // model is already cached by the card that flew here, so onReady lands
      // on a microtask -- still inside this frame, before anything is painted.
      let landed = false;
      const land = () => {
        if (landed) return;
        landed = true;
        clearTimeout(timer);
        target.style.visibility = "";
        cardCanvas.remove();
        renderLoop.remove(viewer);
      };
      const timer = setTimeout(land, LANDING_TIMEOUT);
      mountMascotViewer(land);
    },
  });

  return true;
}

function showDetail(characterId, cardCanvas, direction = 1) {
  // a new character starts facing front, however the last one was left
  resetYawFollow();
  const character = getCharacter(characterId);
  const fromDetail = state.view === "detail";
  state.view = "detail";
  state.detailId = characterId;
  // Three ways in, each falling through to the next so every transition is an
  // enhancement rather than a dependency: flying from a card, sliding between
  // two expanded characters, or a plain mount.
  if (cardCanvas && flyToDetail(character, cardCanvas)) return;
  if (fromDetail && switchDetail(characterId, direction)) return;
  mount(() => buildDetailView(characterId));
}

/* ------------------------------------------------------------------ */
/* Turning the character with the cursor                              */
/* ------------------------------------------------------------------ */

// How much of a turn the swing is worth, and how far the cursor has to travel
// to reach the end of it, as a share of the viewport width.
const YAW_SWING = 0.42;
const YAW_SPAN = 0.33;

// The expanded character follows the cursor, with no hover needed to start it.
//
// What she follows is how far the cursor has moved from where it FIRST moved,
// not where it sits on screen. Absolute position cannot work: the pointer is
// wherever the last click left it when the view opens -- frequently out at the
// rail or over the panel -- and mapping that directly would snap her hard
// round before the cursor had moved at all. Taking the first move as neutral
// means she starts facing front wherever the pointer happens to be, and turns
// only in response to actual movement.
let yawOrigin = null;
let yawFrame = null;
let lastPointer = null;

// Where a touch drag left the character, so the next drag carries on from
// there instead of snapping back to front. Reset with the rest of the yaw
// state whenever the expanded character changes.
let yawHeld = 0;

// called whenever the expanded character changes or the view is left, so the
// next one starts facing front and re-establishes its own neutral
function resetYawFollow() {
  yawOrigin = null;
  lastPointer = null;
  yawHeld = 0;
  const canvas = document.querySelector(".detail-mascot-canvas");
  state.viewers.find((v) => v.canvas === canvas)?.setYawOffset(0);
}

function pointerYaw() {
  yawFrame = null;
  if (lastPointer === null) return;
  const canvas = document.querySelector(".detail-mascot-canvas");
  if (!canvas) return;
  const viewer = state.viewers.find((v) => v.canvas === canvas);
  if (!viewer) return;

  // the first move only establishes neutral -- she does not turn for it
  if (yawOrigin === null) {
    yawOrigin = lastPointer;
    viewer.setYawOffset(0);
    return;
  }

  const span = Math.max(1, window.innerWidth * YAW_SPAN);
  const side = Math.max(-1, Math.min(1, (lastPointer - yawOrigin) / span));
  viewer.setYawOffset(side * YAW_SWING);
}

document.addEventListener(
  "pointermove",
  (event) => {
    // A finger is not a cursor. Following it would mean the character turned
    // as a side effect of every scroll and tap, and only while the finger was
    // down -- so touch gets a drag instead, in attachMascotDrag above.
    if (event.pointerType === "touch") return;
    lastPointer = event.clientX;
    // one update per frame at most; the pointer fires far more often than the
    // page can paint, and this does a DOM lookup
    if (yawFrame === null) yawFrame = requestAnimationFrame(pointerYaw);
  },
  { passive: true }
);

// The Menu button has nothing to open, so it toggles a look at how the page is
// built instead: the whole thing desaturates and every character drops its
// texture for its raw topology.
const menuBtn = document.querySelector(".menu-btn");
let meshMode = false;
menuBtn?.addEventListener("click", () => {
  meshMode = !meshMode;
  document.documentElement.classList.toggle("mesh-mode", meshMode);
  menuBtn.setAttribute("aria-pressed", String(meshMode));
  setMeshMode(meshMode);
  // the materials are shared and the scenes only redraw on demand, so every
  // live viewer has to be told the look changed under it
  for (const viewer of state.viewers) viewer.render();
});

// The grid is built and laid out at once -- the characters need real boxes to
// be measured and drawn into long before anyone sees them -- but it is held
// behind the loading sheet, and its cards are dealt in as that hands the page
// over. First paint only; a grid returned to from the detail view flies its
// card back instead, and one already on screen has nowhere to arrive from.
const firstGrid = showGrid();
onPageIn(() => firstGrid?.dealIn());
